import axios, { AxiosError } from 'axios';
import type { PhoneInventory, PhoneInventoryStatePage } from '../types/phoneInventory';
import { toast } from 'sonner';
import { FULL_ACCESS_MAILTO, featureDeniedFrom, featureDeniedMessage, useFeatureStore } from '../auth/features';
import { clearLegalAcceptanceRecord } from '../legal/acceptance';
import { isPlaceholderId } from '../utils/placeholderNode';
import { useWorkbenchSessionStore } from '../stores/useWorkbenchSessionStore';
import { beginAppNavigation, clearSession, hasActiveSession } from '../utils/session';
import { currentReturnPath, safeReturnPath } from '../utils/loginReturn';
import {
    GUEST_SIGN_IN_MESSAGE,
    isGuestApiAllowed,
    isGuestDemo,
    isGuestDemoPath,
} from '../utils/guestDemo';

// Use environment variable for production, fallback to localhost for dev
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

console.log('API Base URL:', API_BASE_URL);

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 second timeout — prevents UI freezing on network issues
});

export class GuestApiBlockedError extends Error {
    readonly code = 'GUEST_API_BLOCKED';
    constructor(message = GUEST_SIGN_IN_MESSAGE) {
        super(message);
        this.name = 'GuestApiBlockedError';
    }
}

// Add auth token interceptor
api.interceptors.request.use((config) => {
    // Guest sandbox: block non-allowlisted API calls before they hit the network.
    if (isGuestDemo() && !hasActiveSession() && !isGuestApiAllowed(config.url)) {
        return Promise.reject(new GuestApiBlockedError());
    }
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const _featureDeniedToasted = new Set<string>();
let _featureRefreshAt = 0;

function notifyFeatureDenied(feature: string, tier: string): void {
    if (!_featureDeniedToasted.has(feature)) {
        _featureDeniedToasted.add(feature);
        toast.info(featureDeniedMessage(feature, tier), {
            id: `feature-denied-${feature}`,
            action: {
                label: 'Request full access',
                onClick: () => { window.location.href = FULL_ACCESS_MAILTO; },
            },
        });
    }
    const now = Date.now();
    if (now - _featureRefreshAt > 30_000) {
        _featureRefreshAt = now;
        api.get('/auth/me')
            .then((response) => useFeatureStore.getState().setFromProfile(response.data))
            .catch(() => { /* best-effort */ });
    }
}

/**
 * The app's expired-session path: clear the session and send the officer to
 * /login?expired=1. Fires once per page load. Shared by the axios interceptor
 * and the plain-fetch Fusion calls (services/caseCanvas.ts), which bypass it.
 */
export function handleSessionExpired(): void {
    if (_redirectingToLogin) return;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    // Stay on guest demo routes — do not bounce visitors to login for stray 401s.
    if (isGuestDemoPath(path) || path.includes('/login')) return;
    _redirectingToLogin = true;
    console.warn('[auth] Session expired — redirecting to login');
    clearSession({ preservePublicCatalog: true });
    // Tell unsaved-work guards to persist their draft without
    // raising the native "Leave site?" prompt, which would cancel
    // this redirect and leave the app signed out but still mounted.
    beginAppNavigation();
    // Use replace so back button doesn't land on the broken page
    const next = encodeURIComponent(safeReturnPath(currentReturnPath()));
    window.location.replace(`/login?expired=1&next=${next}`);
}

// Add response interceptor to handle 401s (Token Expiry/Invalid)
// Uses a flag to ensure we only redirect once (not once per failed API call)
let _redirectingToLogin = false;
let _redirectingToLegal = false;
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error instanceof GuestApiBlockedError || (error as AxiosError)?.name === 'GuestApiBlockedError') {
            return Promise.reject(error);
        }
        if (error.response && error.response.status === 403) {
            const code = error.response.data?.detail?.code || error.response.data?.code;
            const denied = featureDeniedFrom(error);
            if (denied) {
                // Tier gate, not a session problem: never redirect. One toast
                // per feature per page load, then refresh the cached matrix in
                // case the role changed since /auth/me was read.
                notifyFeatureDenied(denied.feature, denied.tier);
                // Many call sites render `response.data.detail` straight into a
                // toast; give them a sentence instead of the structured object.
                // The structured form stays on `error.featureDenied`.
                (error as { featureDenied?: unknown }).featureDenied = denied;
                error.response.data = { ...error.response.data, detail: featureDeniedMessage(denied.feature, denied.tier) };
                return Promise.reject(error);
            }
            if (code === 'LEGAL_ACCEPTANCE_REQUIRED') {
                clearLegalAcceptanceRecord();
                if (
                    !window.location.pathname.includes('/legal/accept') &&
                    !_redirectingToLegal
                ) {
                    _redirectingToLegal = true;
                    window.location.replace('/legal/accept');
                }
            }
        }
        if (error.response && error.response.status === 401) {
            handleSessionExpired();
        }
        return Promise.reject(error);
    }
);

export const feedbackService = {
    submit: async (chain: string, address: string, isAccurate: boolean, comments?: string, investigationData?: any) => {
        const response = await api.post('/feedback', {
            chain,
            address,
            is_accurate: isAccurate,
            comments,
            investigation_data: investigationData,
            page_url: window.location.href,
        });
        return response.data;
    },
    submitGeneral: async (feedbackType: string, comments: string) => {
        const response = await api.post('/feedback/general', {
            feedback_type: feedbackType,
            comments,
            page_url: window.location.href,
        });
        return response.data;
    },
    list: async (status?: string, limit = 50) => {
        const response = await api.get('/feedback', { params: { status, limit } });
        return response.data;
    },
    respond: async (feedbackId: number, adminResponse: string) => {
        const response = await api.patch(`/feedback/${feedbackId}/respond`, {
            admin_response: adminResponse,
        });
        return response.data;
    },
};

export type NotificationCategory =
    | 'watchlist_alert'
    | 'feedback_reply'
    | 'case_shared'
    | 'import_complete'
    | 'auto_case'
    | 'scraper_run';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export interface InboxNotification {
    id: number;
    user_id: number;
    category: NotificationCategory;
    severity: NotificationSeverity;
    title: string;
    body: string;
    case_id: string | null;
    investigation_id: string | null;
    resource_type: string | null;
    resource_id: string | null;
    metadata: Record<string, unknown>;
    source_key: string | null;
    created_at: string;
    read_at: string | null;
    unread: boolean;
}

export const notificationService = {
    list: async (params?: { limit?: number; unread?: boolean; category?: NotificationCategory }) => {
        const response = await api.get('/notifications', { params });
        return response.data as { items: InboxNotification[]; total: number; unread_count: number };
    },
    unreadCount: async () => {
        const response = await api.get('/notifications/unread-count');
        return response.data as { unread_count: number };
    },
    markRead: async (id: number) => {
        const response = await api.post(`/notifications/${id}/read`);
        return response.data;
    },
    markAllRead: async () => {
        const response = await api.post('/notifications/read-all');
        return response.data as { status: string; updated: number };
    },
};

/** Platform product updates / version newsletter (Settings → What's New). */
export type ProductUpdateBullet = { label?: string; text: string };
export type ProductUpdate = {
    id: number;
    version_label: string | null;
    title: string;
    summary: string | null;
    bullets: ProductUpdateBullet[];
    category: string;
    status: 'draft' | 'published' | string;
    released_at: string | null;
    published_at: string | null;
    email_sent_at: string | null;
    email_status: string | null;
    email_error: string | null;
    email_recipient: string | null;
    created_by: number | null;
    created_at: string | null;
    updated_at: string | null;
};

export const productUpdateService = {
    list: async (includeDrafts = false, limit = 50) => {
        const response = await api.get('/product-updates', {
            params: { include_drafts: includeDrafts, limit },
        });
        return response.data as {
            updates: ProductUpdate[];
            count: number;
            newsletter_to: string;
        };
    },
    create: async (data: {
        title: string;
        summary?: string;
        bullets?: ProductUpdateBullet[] | string[];
        category?: string;
        version_label?: string;
        released_at?: string;
    }) => {
        const response = await api.post('/product-updates', data);
        return response.data as ProductUpdate;
    },
    publish: async (id: number, opts?: { send_email?: boolean; force_email?: boolean }) => {
        const response = await api.post(`/product-updates/${id}/publish`, opts || {});
        return response.data as {
            success: boolean;
            update: ProductUpdate;
            email?: { success: boolean; message?: string; recipient?: string };
        };
    },
    send: async (id: number, opts?: { force?: boolean; to_email?: string }) => {
        const response = await api.post(`/product-updates/${id}/send`, opts || {});
        return response.data as {
            email: { success: boolean; message?: string; recipient?: string };
            update: ProductUpdate;
        };
    },
    remove: async (id: number) => {
        const response = await api.delete(`/product-updates/${id}`);
        return response.data as { success: boolean; id: number };
    },
};

/**
 * Generic placeholder-skip stub for `addressService` per-address methods.
 *
 * Synthesized canvas-node ids — `ghost:`, `cluster:`, `__coinbase__`,
 * `attr-hub:`, etc. (see `utils/placeholderNode.ts`) — aren't real on-chain
 * addresses. Sending them to `/addresses/{chain}/{address}/*` returns 422
 * every time and pollutes both backend logs and the dossier auto-load
 * pipeline, which then re-tries on accordion expand. Each per-address
 * method does:
 *
 *     if (isPlaceholderId(address)) return _placeholderStub();
 *
 * Callers either optional-chain the fields they care about (works fine —
 * undefined) or check `_skipped_placeholder`.
 */
const _placeholderStub = <T extends Record<string, any>>(extra?: T) => ({
    _skipped_placeholder: true as const,
    ...(extra || {} as T),
});

const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A case id worth sending on a stream URL: a UUID, trimmed; anything else is not sent. */
export function holisticCaseId(caseId: string | null | undefined): string | null {
    const v = typeof caseId === 'string' ? caseId.trim() : '';
    return _UUID_RE.test(v) ? v : null;
}

export const addressService = {
    /**
     * Probe an address against multiple chain RPCs to disambiguate which
     * chain holds activity. Returns instantly for unambiguous formats
     * (TRX, BTC, DOGE, LTC, etc.); takes ~500ms for EVM-format addresses.
     * Cached forever after first probe (chain membership is stable).
     *
     * Use this from the omnibar BEFORE getDetails/getTriage when
     * `detectChainCandidates(addr).length > 1`.
     */
    probeChain: async (address: string, refresh = false): Promise<{
        address: string;
        candidates: string[];
        chosen_chain: string | null;
        chosen_reason: string;
        activity_by_chain: Record<string, { nonce: number; balance_wei: number; is_contract: boolean; has_activity: boolean; error?: string | null }>;
        duration_ms: number;
        from_cache: boolean;
    }> => {
        const response = await api.get('/chain-probe', { params: { address, refresh: refresh || undefined } });
        return response.data;
    },

    getDetails: async (chain: string, address: string, caseId?: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}`, {
            params: { case_id: caseId || undefined }
        });
        return response.data;
    },

    getTransactions: async (
        chain: string,
        address: string,
        limit = 100,
        offset = 0,
        startDate?: string,
        endDate?: string,
        debug?: boolean,
        caseId?: string,
        options?: { includeAcceleration?: boolean; cacheOnly?: boolean },
    ) => {
        // Synthesized-id guard: ghost placeholders (`ghost:{uuid}:source`) and
        // chain-namespaced placeholders (`__coinbase__:btc`, etc.) are not real
        // addresses — fetching them returns 422 every time and pollutes logs.
        // Bulk enrichment paths (post-investigation enrich, find-paths, etc.)
        // would otherwise iterate every canvas node and burst these.
        if (isPlaceholderId(address)) {
            return { transactions: [], total: 0, _skipped_placeholder: true };
        }
        // Upstream throttling (Etherscan adaptive RPS, Arkham retries) plus
        // entity enrichment + cache write can push a heavy-address fetch past
        // 60s. 120s leaves headroom now that Neo4j writes are fire-and-forget
        // (Apr 2026) so the critical path is just TronGrid/Etherscan + entity
        // enrichment + cache.set.
        // Acceleration (patterns/exposure/CP) is opt-in — expand uses it;
        // inspector auto-load and count/date loaders stay lean.
        const response = await api.get(`/addresses/${chain}/${address}/transactions`, {
            params: {
                limit,
                offset,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                debug: debug || undefined,
                case_id: caseId || undefined,
                include_acceleration: options?.includeAcceleration === true ? true : false,
                cache_only: options?.cacheOnly === true ? true : undefined,
            },
            // Under Railway's ~100s proxy budget so a hang becomes a retryable
            // axios timeout instead of a silent connection drop.
            timeout: 90_000,
        });
        return response.data;
    },

    getTriage: async (chain: string, address: string, depth = 1, options?: { fast?: boolean }) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        // Triage can stall when upstream provider RPS is throttled (Etherscan adaptive
        // slowdown, Arkham circuit breaker). 60s covers the 99th percentile; below that
        // the user sees ECONNABORTED even when the backend is about to return.
        const response = await api.get(`/addresses/${chain}/${address}/triage`, {
            params: { depth, fast: options?.fast || undefined },
            timeout: 60000,
        });
        return response.data;
    },

    trace: async (chain: string, address: string, hops = 3) => {
        const response = await api.post('/addresses/trace', {
            chain,
            address,
            max_hops: hops,
            direction: 'forward' // or 'both'
        }, { timeout: 300_000 }); // 5 min — BFS trace can take minutes for deep graphs
        return response.data;
    },

    tracePath: async (chain: string, source: string, target: string) => {
        const response = await api.get(`/addresses/${chain}/trace-path`, {
            params: { source, target },
            // LINKS does a direct-pair probe + cold bidirectional multi-hop
            // expansion; on an uncached pair that legitimately exceeds the 30s
            // api.ts default. Without this the request aborted mid-probe and the
            // caller recorded a false "no link" it then refused to retry.
            timeout: 120000,
        });
        return response.data;
    },

    edgesBetween: async (chain: string, addresses: string[], limit = 500) => {
        const response = await api.post('/addresses/edges-between', {
            chain, addresses, limit,
        });
        return response.data as Array<{
            from_address: string;
            to_address: string;
            tx_hash: string | null;
            value: number | null;
            timestamp: string | null;
            token_symbol: string | null;
        }>;
    },

    /**
     * AI explanation of the current graph. When the model is unavailable the
     * backend answers `status: "unavailable"`, `analysis: null` and `ai_error`;
     * successful answers carry `ai_meta` (generation id, provider, model).
     * Callers must treat a null `analysis` as "AI unavailable", never as text.
     */
    explainInvestigation: async (
        graphData: any,
        riskReport: any,
        chain?: string,
        opts?: { caseId?: string | null; focusAddress?: string | null },
    ) => {
        const response = await api.post('/analytics/explain', {
            graph_data: graphData,
            risk_report: riskReport,
            chain,
            // Optional: lets the server compute facts for the right subject and
            // attach the generation to the case in its audit record.
            ...(opts?.caseId ? { case_id: opts.caseId } : {}),
            ...(opts?.focusAddress ? { focus_address: opts.focusAddress } : {}),
        }, { timeout: 60000 }); // 60s — backend AI explain + signal assembly can take 25-30s
        return response.data;
    },

    getRisk: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/risk`);
        return response.data;
    },

    autoTrace: async (chain: string, address: string) => {
        const response = await api.get(`/addresses/${chain}/${address}/auto-trace`, {
            timeout: 60_000, // Edge enrichment for 10 paths × 3 hops can take 20-30s
        });
        return response.data;
    },

    /**
     * SSE streaming auto-trace to exchange.
     * Calls `onEvent` for each SSE event (phase, progress, node_discovered,
     * edge_discovered, complete, error, heartbeat).
     * Returns a cleanup function that closes the EventSource.
     */
    autoTraceStream: (
        chain: string,
        address: string,
        onEvent: (eventType: string, data: any) => void,
    ): (() => void) => {
        const token = localStorage.getItem('token');
        const base = (api.defaults.baseURL || '').replace(/\/$/, '');
        const url = `${base}/addresses/${chain}/${address}/auto-trace/stream?max_depth=4${token ? `&token=${encodeURIComponent(token)}` : ''}`;
        const es = new EventSource(url);

        const EVENTS = ['phase', 'progress', 'node_discovered', 'edge_discovered', 'complete', 'error', 'heartbeat'];
        for (const evt of EVENTS) {
            es.addEventListener(evt, (e: Event) => {
                try {
                    const data = JSON.parse((e as MessageEvent).data || '{}');
                    onEvent(evt, data);
                } catch {
                    onEvent(evt, {});
                }
            });
        }
        es.onerror = () => {
            onEvent('error', { error: 'SSE connection failed' });
            es.close();
        };

        return () => es.close();
    },

    /**
     * SSE streaming holistic trace for multiple nodes.
     */
    holisticTraceStream: (
        chain: string,
        addresses: string[],
        trace_hops: number = 2,
        onEvent: (eventType: string, data: any) => void,
        opts?: { caseId?: string | null },
    ): (() => void) => {
        const token = localStorage.getItem('token');
        const base = (api.defaults.baseURL || '').replace(/\/$/, '');
        const addrParam = addresses.map(a => encodeURIComponent(a)).join(',');
        // The case the Workbench is in labels the holistic narrative's AI audit row (case report disclosures).
        // Without an explicit id, the case the Workbench session is in (set when it opens from a case).
        const caseId = holisticCaseId(opts?.caseId !== undefined ? opts.caseId : useWorkbenchSessionStore.getState().caseId);
        const url = `${base}/investigation/holistic/stream?chain=${encodeURIComponent(chain)}&addresses=${addrParam}&trace_hops=${trace_hops}${caseId ? `&case_id=${encodeURIComponent(caseId)}` : ''}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
        const es = new EventSource(url);

        const EVENTS = ['step_start', 'step_result', 'progress', 'node_discovered', 'edge_discovered', 'complete', 'error', 'heartbeat'];
        for (const evt of EVENTS) {
            es.addEventListener(evt, (e: Event) => {
                try {
                    const data = JSON.parse((e as MessageEvent).data || '{}');
                    onEvent(evt, data);
                } catch {
                    onEvent(evt, {});
                }
            });
        }
        es.onerror = () => {
            onEvent('error', { message: 'Connection lost or server error' });
            es.close();
        };

        return () => es.close();
    },

    findCompliantExchange: async (chain: string, address: string) => {
        const response = await api.get(`/addresses/${chain}/${address}/compliant-exchange`, {
            timeout: 60_000,
        });
        return response.data;
    },

    findCompliantExchangeStream: (
        chain: string,
        address: string,
        onEvent: (eventType: string, data: any) => void,
    ): (() => void) => {
        const token = localStorage.getItem('token');
        const base = (api.defaults.baseURL || '').replace(/\/$/, '');
        const url = `${base}/addresses/${chain}/${address}/compliant-exchange/stream?max_depth=10${token ? `&token=${encodeURIComponent(token)}` : ''}`;
        const es = new EventSource(url);

        const EVENTS = ['phase', 'progress', 'node_discovered', 'edge_discovered', 'complete', 'error', 'heartbeat'];
        for (const evt of EVENTS) {
            es.addEventListener(evt, (e: Event) => {
                try {
                    const data = JSON.parse((e as MessageEvent).data || '{}');
                    onEvent(evt, data);
                } catch {
                    onEvent(evt, {});
                }
            });
        }
        es.onerror = () => {
            onEvent('error', { error: 'SSE connection failed' });
            es.close();
        };

        return () => es.close();
    },

    getExposure: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/exposure`);
        return response.data;
    },

    // Crystal Intelligence (on-demand, limited quota)
    crystalRiskCheck: async (chain: string, address: string, forceRefresh = false) => {
        const response = await api.post('/addresses/crystal/risk-check', { address, chain, force_refresh: forceRefresh });
        return response.data;
    },
    crystalQuota: async () => {
        const response = await api.get('/addresses/crystal/quota');
        return response.data;
    },
    crystalCached: async (limit = 50) => {
        const response = await api.get(`/addresses/crystal/cached?limit=${limit}`);
        return response.data;
    },

    getClustering: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/clustering`);
        return response.data;
    },

    getCounterparties: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/counterparties`);
        return response.data;
    },

    getCrossChainActivity: async (chain: string, address: string): Promise<{
        chains: Array<{
            chain_name: string;
            chain_label: string;
            internal_chain: string | null;
            last_seen_at: string | null;
            supported: boolean;
            is_testnet: boolean;
        }>;
        total_chains: number;
        supported_count: number;
        note?: string;
        error?: string;
    }> => {
        if (isPlaceholderId(address)) {
            return { chains: [], total_chains: 0, supported_count: 0, _skipped_placeholder: true } as any;
        }
        const response = await api.get(`/addresses/${chain}/${address}/cross-chain-activity`);
        return response.data;
    },

    // Chain-hopping sequence detection — multi-chain fraud flow finder.
    // Operates on already-enriched transactions (bridge_detection +
    // privacy_detection primitives). Zero external calls.
    getChainHopping: async (chain: string, address: string, opts?: {
        timeWindowDays?: number; minHops?: number;
    }): Promise<{
        address: string;
        chain: string;
        sequences: Array<{
            starting_chain: string;
            starting_address: string;
            hops: Array<{
                kind: 'bridge' | 'mixer' | 'cex_onramp' | 'cex_offramp';
                family: string;
                name: string | null;
                source_chain: string;
                target_chain: string | null;
                tx_hash: string;
                timestamp: string | null;
                value: number;
                token_symbol: string | null;
                confidence: 'high' | 'medium' | 'low';
                sanctioned: boolean;
            }>;
            hop_count: number;
            distinct_chains: string[];
            has_mixer_hop: boolean;
            has_sanctioned_hop: boolean;
            recommended_next_step: string | null;
            confidence: 'high' | 'medium' | 'low';
        }>;
        total_sequences: number;
        any_mixer_hop: boolean;
        any_sanctioned_hop: boolean;
        error?: string;
    }> => {
        if (isPlaceholderId(address)) {
            return { sequences: [], total_sequences: 0, any_mixer_hop: false, any_sanctioned_hop: false, _skipped_placeholder: true } as any;
        }
        const params: Record<string, number> = {};
        if (opts?.timeWindowDays) params.time_window_days = opts.timeWindowDays;
        if (opts?.minHops) params.min_hops = opts.minHops;
        const response = await api.get(
            `/addresses/${chain}/${address}/chain-hopping`,
            { params },
        );
        return response.data;
    },

    // Nansen related-wallets — on-demand, user-triggered. Nansen free tier
    // has only 100 total credits, so never auto-call this from enrichment.
    getRelatedWallets: async (chain: string, address: string): Promise<{
        related: Array<{
            address: string;
            address_label: string | null;
            relation: string | null;         // "First Funder", "Multisig Signer of", "Deposit", ...
            transaction_hash: string | null;
            block_number: number | null;
            timestamp: string | null;
        }>;
        total?: number;
        source: 'api' | 'cache' | 'plan_gated' | 'api_error' | 'skipped';
        note?: string;
        error?: string;
        status?: number;
    }> => {
        if (isPlaceholderId(address)) {
            return { related: [], source: 'skipped', _skipped_placeholder: true } as any;
        }
        const response = await api.get(`/addresses/${chain}/${address}/related-wallets`);
        return response.data;
    },

    // ── Phase 3 New Endpoints ───────────────────────────────────
    getProfile: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        // Profile derives from tx history — can be slow; keep under dossier SECTION_TIMEOUT (20s)
        // so the UI settles instead of spinning against the global 30s axios default.
        const response = await api.get(`/addresses/${chain}/${address}/profile`, { timeout: 25_000 });
        return response.data;
    },

    checkSanctions: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ hits: [], summary: null });
        const response = await api.get(`/addresses/${chain}/${address}/sanctions`);
        return response.data;
    },

    batchSanctions: async (addresses: string[]) => {
        // Use POST to avoid URL length limits with many addresses (each ~42 chars)
        const response = await api.post(`/addresses/sanctions/batch`, { addresses });
        return response.data;
    },

    getTokenSecurity: async (chain: string, address: string, tokenAddress: string) => {
        if (isPlaceholderId(address) || isPlaceholderId(tokenAddress)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/token-security/${tokenAddress}`);
        return response.data;
    },

    getApprovals: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ approvals: [] });
        const response = await api.get(`/addresses/${chain}/${address}/approvals`);
        return response.data;
    },

    getBridges: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ bridges: [] });
        const response = await api.get(`/addresses/${chain}/${address}/bridges`);
        return response.data;
    },

    getNonceGaps: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ gaps: [] });
        const response = await api.get(`/addresses/${chain}/${address}/nonce-gaps`);
        return response.data;
    },

    getContractInfo: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub();
        const response = await api.get(`/addresses/${chain}/${address}/contract-info`);
        return response.data;
    },

    getNftTransfers: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ nfts: [] });
        const response = await api.get(`/addresses/${chain}/${address}/nfts`);
        return response.data;
    },

    getDexSwaps: async (chain: string, address: string) => {
        if (isPlaceholderId(address)) return _placeholderStub({ swaps: [] });
        const response = await api.get(`/addresses/${chain}/${address}/dex-swaps`);
        return response.data;
    },

    // Graph algorithms answer status "not_computed" (engine null, empty results, a reason) when there is
    // nothing to compute on; read them with utils/graphAlgorithmStatus, never as an empty ranking.
    getPageRank: async (focusAddress?: string) => {
        const response = await api.get('/addresses/graph/pagerank', { params: { focus_address: focusAddress } });
        return response.data;
    },

    getCommunities: async () => {
        const response = await api.get('/addresses/graph/communities');
        return response.data;
    },

    getEthPrice: async () => {
        const response = await api.get('/addresses/eth-price');
        return response.data;
    },

    getSanctionsStats: async () => {
        const response = await api.get('/addresses/sanctions/stats');
        return response.data;
    }
};

export const watchlistService = {
    getAll: async (caseId?: string) => {
        const response = await api.get('/watchlist', { params: { case_id: caseId } });
        return response.data.watchlist || []; // Extract .watchlist and fallback to []
    },

    add: async (address: string, chain: string, label?: string, notes?: string, caseId?: string, alertEnabled?: boolean, alertCriteria?: any, alertEmail?: string) => {
        const response = await api.post('/watchlist', {
            address,
            chain,
            label,
            notes,
            case_id: caseId,
            alert_enabled: alertEnabled || false,
            alert_criteria: alertCriteria || null,
            alert_email: alertEmail || null,
        });
        return response.data;
    },

    remove: async (address: string) => {
        const response = await api.delete(`/watchlist/${address}`);
        return response.data;
    },

    update: async (address: string, updates: { label?: string; notes?: string; is_active?: boolean; alert_enabled?: boolean; alert_email?: string | null; alert_criteria?: Record<string, any> }) => {
        const response = await api.patch(`/watchlist/${address}`, updates);
        return response.data;
    },

    check: async (address: string) => {
        const response = await api.get(`/watchlist/check/${address}`);
        return response.data;
    },

    getAlertEvents: async (limit = 50, address?: string) => {
        const response = await api.get('/watchlist/alert-events', { params: { limit, address } });
        return response.data;
    },

    triggerAlertCheck: async () => {
        const response = await api.post('/watchlist/alert-check');
        return response.data;
    },
};

/** What `POST /cases/{id}/promote` wrote, and every element it refused with the reason. */
export interface CasePromoteResult {
    case_id: string;
    source: string;
    nodes_written: number;
    edges_written: number;
    transfers_written: number;
    skipped: Array<{ ref?: string; reason?: string }>;
    /** Node and edge counts of the Flow IR the server built from the request. */
    flow_nodes: number;
    flow_edges: number;
    event_id: string | null;
}

/** `{source: 'orchestrator', results, chain, subject}` or `{source, flow}`; exactly one of results / flow. */
export interface CasePromoteBody {
    source: string;
    results?: Record<string, any>;
    chain?: string;
    subject?: string;
    flow?: Record<string, any>;
    investigation_id?: string;
}

export const casePromoteService = {
    /** "Promote to case": write a completed result's Flow IR to the case spine (gated `cases.own`). */
    promote: async (caseId: string, body: CasePromoteBody): Promise<CasePromoteResult> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/promote`, body, { timeout: 60000 });
        return response.data;
    },
};

export const flagService = {
    create: async (address: string, chain: string, flagType: string = 'suspicious', reason?: string, riskOverride?: number, caseId?: string) => {
        const response = await api.post('/flags', {
            address, chain, flag_type: flagType, reason, risk_override: riskOverride, case_id: caseId,
        });
        return response.data;
    },
    list: async () => {
        const response = await api.get('/flags');
        return response.data;
    },
    remove: async (address: string) => {
        const response = await api.delete(`/flags/${address}`);
        return response.data;
    },
    check: async (address: string) => {
        const response = await api.get(`/flags/check/${address}`);
        return response.data;
    },
};

export const bulkImportService = {
    uploadCSV: async (file: File, caseId?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (caseId) {
            formData.append('case_id', caseId);
        }
        const response = await api.post('/watchlist/bulk-import/csv', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    createJob: async (addresses: Array<{ address: string; chain: string; label?: string }>, caseId?: string) => {
        const response = await api.post('/watchlist/bulk-import', {
            addresses,
            case_id: caseId
        });
        return response.data;
    },

    processJob: async (jobId: string) => {
        const response = await api.post(`/watchlist/bulk-import/${jobId}/process`);
        return response.data;
    },

    getJobStatus: async (jobId: string) => {
        const response = await api.get(`/watchlist/bulk-import/${jobId}`);
        return response.data;
    },

    getLatest: async (limit = 5) => {
        const response = await api.get('/watchlist/bulk-import', { params: { limit } });
        return response.data;
    }
};

export const entityService = {
    getAll: async (params?: {
        category?: string;
        subcategory?: string;
        source?: string;
        risk_level?: string;
        country?: string;
        search?: string;
        sort_by?: string;
        has_recent_activity?: boolean;
        limit?: number;
        offset?: number;
    }, opts?: { signal?: AbortSignal }) => {
        const response = await api.get('/entities', { params, timeout: 15_000, signal: opts?.signal });
        return response.data;
    },

    getCount: async (params?: {
        category?: string;
        subcategory?: string;
        source?: string;
        risk_level?: string;
        country?: string;
        search?: string;
        has_recent_activity?: boolean;
    }, opts?: { signal?: AbortSignal }) => {
        const response = await api.get('/entities/count', { params, timeout: 60_000, signal: opts?.signal });
        return response.data;
    },

    search: async (query: string) => {
        const response = await api.get('/entities/search', { params: { q: query } });
        return response.data;
    },

    getStats: async () => {
        const response = await api.get('/entities/stats', { timeout: 60_000 });
        return response.data;
    },

    /** Exchange brand clusters for the Entities-page accordion. Backend
     *  serves a precomputed summary table (pre-warmed on boot); allow up to
     *  90s so a cold miss can finish the single-flight rollup instead of
     *  surfacing "Failed to load exchange intelligence." */
    getExchangeIntelligence: async () => {
        const response = await api.get('/entities/exchange-intelligence', { timeout: 90_000 });
        return response.data;
    },

    lookup: async (address: string, chain?: string) => {
        const response = await api.get(`/entities/lookup/${address}`, { params: { chain } });
        return response.data;
    },

    batchLookup: async (
        addresses: Array<{ address: string; chain: string }>,
        opts?: { deep?: boolean },
    ) => {
        // `deep=true` opts in to the slow-path provider (Arkham /intelligence,
        // ~5s/address) AND bypasses the negative cache. Use only on explicit
        // user-triggered enrichment (Inspect → Entity Lookup button), NOT on
        // automatic flows like canvas node-add or graph filter changes.
        const response = await api.post('/entities/batch-lookup', {
            addresses,
            deep: opts?.deep ?? false,
        }, {
            // Deep mode walks Arkham (5s/addr) — bump timeout when requested.
            timeout: opts?.deep ? 60000 : 30000,
        });
        return response.data;
    },

    upsertManualLabel: async (data: { address: string; chain: string; label?: string; labels?: string[]; category?: string; risk_level?: string; description?: string; tags?: string[]; intelligence_attributions?: Array<Record<string, any>> }) => {
        const response = await api.post('/entities/manual', data);
        return response.data;
    }
};

export const caseService = {
    getAll: async () => {
        const response = await api.get('/cases');
        return response.data;
    },

    get: async (id: string) => {
        const response = await api.get(`/cases/${id}`);
        return response.data;
    },

    /** Consolidated view of one case-worker run (`runId` may be "latest"). */
    getConsolidated: async (caseId: string, runId: string = 'latest'): Promise<import('../case-canvas/consolidated/types').ConsolidatedResponse> => {
        const response = await api.get(
            `/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/consolidated`,
            { timeout: 60000 },
        );
        return response.data;
    },

    create: async (data: { title: string; fir_number?: string; officer_name?: string; notes?: string }) => {
        const response = await api.post('/cases', data);
        return response.data;
    },

    update: async (id: string, updates: any) => {
        const response = await api.patch(`/cases/${id}`, updates);
        return response.data;
    },

    delete: async (id: string) => {
        const response = await api.delete(`/cases/${id}`);
        return response.data;
    },

    autoInvestigate: async (id: string, payload?: { seeds?: string[]; case_title?: string; notes?: string }) => {
        // Same endpoint as caseWorkerService.autoInvestigateCase — the server-side
        // fan-out has a 600 s wall-clock budget, so the 30 s default would abort it.
        const response = await api.post(`/case-worker/cases/${id}/auto-investigate`, payload || {}, { timeout: 600000 });
        return throwIfStreamedRunError(response.data);
    },

    ingestMultimodal: async (id: string, payload: { raw_text: string; evidence_source?: string; suspect_name?: string }) => {
        const response = await api.post(`/cases/${id}/ingest-multimodal`, payload);
        return response.data;
    },

    getWatch: async (id: string) => {
        const response = await api.get(`/cases/${id}/watch`);
        return response.data;
    },

    setWatch: async (id: string, enabled: boolean) => {
        const response = await api.put(`/cases/${id}/watch`, { enabled });
        return response.data;
    },

    // getCanonicalGraph removed — the Fusion box reads the case-graph spine via
    // fetchCaseGraph(caseId, { level: 'high' | 'low' }) in services/caseCanvas.ts
    // (GET /canvas/{id}/graph?level=…); the parallel canonical-graph model has
    // been retired.

    parseForensicFile: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        // The rich 8-method walker (OCR / SQLite / nested archives) is slower
        // than the old byte scan — allow up to 5 min, matching the BFS trace
        // override, so large extractions don't hit the 30s default.
        const response = await api.post('/cases/parse-forensic-file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000,
        });
        return response.data;
    },

    // Dry-run: classify + count a forensic container without extracting.
    // Returns an InventoryReport for the UI's plan step + progress ETA.
    forensicInventory: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/cases/forensic-inventory', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
        return response.data;
    },

    // Detection catalog — what CryptoScan detects (file-decode methods + artefact
    // types), for the select/deselect table. Single source of truth shared with
    // the CLI + offline UI. Returns { detectors: [{id,label,description,kind,
    // category,default_enabled,gateable}, ...] }.
    detectionCatalog: async () => {
        const response = await api.get('/cases/detection-catalog');
        return response.data;
    },

    // Relevance-annotated planner tree + guidance for a container (shared engine
    // with the offline tool). Built from the archive index — nothing extracted.
    // Returns a FolderTree: { source, kind, root, guidance[], total_files, ... }.
    forensicTree: async (file: File, options?: Record<string, any> | null) => {
        const formData = new FormData();
        formData.append('file', file);
        if (options) formData.append('options', JSON.stringify(options));
        const response = await api.post('/cases/forensic-tree', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
        return response.data;
    },

    // Streamed forensic parse: upload + options -> job_id, then SSE progress.
    // `options` is the extraction plan (method selection + budgets). onEvent
    // receives ('start'|'file_done'|'budget_stop'|'heartbeat'|'complete'|'error', data).
    // The `complete` payload carries `partial` / `partial_reason` when a budget ended the scan early.
    // Returns a cancel function (closes the stream). Falls back to a thrown
    // error if the start call fails (caller can retry the blocking parse).
    parseForensicStream: async (
        file: File,
        options: Record<string, any> | null,
        onEvent: (eventType: string, data: any) => void,
    ): Promise<() => void> => {
        const formData = new FormData();
        formData.append('file', file);
        if (options) formData.append('options', JSON.stringify(options));
        const startRes = await api.post('/cases/forensic-parse/start', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
        const jobId = startRes.data?.job_id;
        if (!jobId) throw new Error('No job_id returned from forensic-parse/start');

        const base = (api.defaults.baseURL || '').replace(/\/$/, '');
        // EventSource cannot set an Authorization header: pass the JWT as ?token= like the other streams.
        const token = localStorage.getItem('token');
        const es = new EventSource(
            `${base}/cases/forensic-parse/stream?job_id=${encodeURIComponent(jobId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`,
        );
        const EVENTS = ['start', 'file_done', 'budget_stop', 'heartbeat', 'complete', 'error'];
        for (const evt of EVENTS) {
            es.addEventListener(evt, (e: Event) => {
                try { onEvent(evt, JSON.parse((e as MessageEvent).data || '{}')); }
                catch { onEvent(evt, {}); }
                if (evt === 'complete' || evt === 'error') es.close();
            });
        }
        es.onerror = () => { onEvent('error', { message: 'SSE connection failed' }); es.close(); };
        return () => es.close();
    },

    // Re-ingest an offline-produced NDJSON manifest into a case. Verifies the
    // ed25519 signature server-side, persists evidence, and returns the same
    // extracted_addresses shape as parseForensicFile so the caller can drop the
    // wallets onto the canvas.
    ingestManifest: async (caseId: string, file: File, opts?: { investigationId?: string; notes?: string }) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams();
        if (opts?.investigationId) params.set('investigation_id', opts.investigationId);
        if (opts?.notes) params.set('notes', opts.notes);
        const qs = params.toString();
        const response = await api.post(
            `/forensic/${caseId}/ingest-manifest${qs ? `?${qs}` : ''}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
        );
        return response.data;
    },

    trackForensicTelemetry: async (payload: {
        event_name: string;
        file_name?: string;
        file_size_mb?: number;
        max_allowed_mb?: number;
        extension?: string;
        source?: string;
    }) => {
        const response = await api.post('/cases/forensic-telemetry', payload);
        return response.data;
    },

    importAddresses: async (data: { case_id?: string; case_metadata?: any; addresses: { address: string; chain: string }[] }) => {
        const response = await api.post('/cases/import-addresses', data);
        return response.data;
    },

    csvImport: async (file: File, opts?: {
        caseId?: string;
        caseTitle?: string;
        firNumber?: string;
        investigationName?: string;
    }) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams();
        if (opts?.caseId) params.set('case_id', opts.caseId);
        if (opts?.caseTitle) params.set('case_title', opts.caseTitle);
        if (opts?.firNumber) params.set('fir_number', opts.firNumber);
        if (opts?.investigationName) params.set('investigation_name', opts.investigationName);
        const response = await api.post(
            `/cases/csv-import?${params.toString()}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    },

    addTransactions: async (caseId: string, transactions: Array<{ tx_hash: string; chain: string; wallet_address?: string; amount?: string; tx_date?: string; notes?: string }>) => {
        const response = await api.post(`/cases/${caseId}/transactions`, transactions);
        return response.data;
    },

    listTransactions: async (caseId: string) => {
        const response = await api.get(`/cases/${caseId}/transactions`);
        return response.data;
    },

    runRetroScan: async () => {
        const response = await api.post('/cases/retro-scan');
        return response.data;
    },

    listCollaborators: async (caseId: string) => {
        const response = await api.get(`/cases/${caseId}/collaborators`);
        return response.data as Array<{
            user_id: number;
            username?: string;
            full_name?: string;
            role: string;
            added_by?: number;
            added_at?: string;
        }>;
    },

    addCollaborator: async (caseId: string, userId: number, role: string = 'viewer') => {
        const response = await api.post(`/cases/${caseId}/collaborators`, { user_id: userId, role });
        return response.data;
    },

    removeCollaborator: async (caseId: string, userId: number) => {
        const response = await api.delete(`/cases/${caseId}/collaborators/${userId}`);
        return response.data;
    },

    listEvidence: async (caseId: string) => {
        const response = await api.get(`/cases/${caseId}/evidence`);
        return response.data;
    },

    addEvidence: async (
        caseId: string,
        payload: {
            evidence_type: string;
            title: string;
            description?: string;
            payload?: Record<string, any>;
            source?: string;
            source_ref?: string;
            collected_by?: string;
            collected_at?: string;
            hash_sha256?: string;
        }
    ) => {
        const response = await api.post(`/cases/${caseId}/evidence`, payload);
        return response.data;
    },

    updateEvidence: async (caseId: string, evidenceId: number, updates: Record<string, any>) => {
        const response = await api.patch(`/cases/${caseId}/evidence/${evidenceId}`, updates);
        return response.data;
    },

    deleteEvidence: async (caseId: string, evidenceId: number) => {
        const response = await api.delete(`/cases/${caseId}/evidence/${evidenceId}`);
        return response.data;
    },

    exportCase: async (caseId: string, format: 'pdf' | 'json' | 'csv' = 'pdf') => {
        const response = await api.get(`/cases/${caseId}/export`, {
            params: { format },
            responseType: 'blob'
        });

        return {
            blob: response.data as Blob,
            sha256: response.headers?.['x-export-sha256'] as string | undefined,
            contentType: response.headers?.['content-type'] as string | undefined,
            contentDisposition: response.headers?.['content-disposition'] as string | undefined,
        };
    }
};

export const p2pService = {
    getUPIProfile: async (upiId: string) => {
        const response = await api.get(`/p2p/upi/${upiId}`);
        return response.data;
    },

    ingestAd: async (data: any) => {
        const response = await api.post('/p2p/ingest', data);
        return response.data;
    },

    linkUpiToWallet: async (upiId: string, address: string, source?: string, notes?: string) => {
        const response = await api.post(`/p2p/upi/${encodeURIComponent(upiId)}/link`, {
            address,
            source: source || 'manual_investigation',
            notes
        });
        return response.data;
    },

    unlinkUpiFromWallet: async (upiId: string, address: string) => {
        const response = await api.delete(`/p2p/upi/${encodeURIComponent(upiId)}/link`, {
            data: { address }
        });
        return response.data;
    },

    getWalletLinkedUPIs: async (address: string) => {
        const response = await api.get(`/p2p/wallet/${encodeURIComponent(address)}/linked-upis`);
        return response.data;
    },

    autoLinkIdentities: async () => {
        const response = await api.post('/p2p/auto-link-identities');
        return response.data;
    }
};

// MuleHunter Lite — read-only unified index of identifiers the platform already
// extracts (Telegram + P2P + Instagram), cross-linked. Reads for any user;
// backfill is admin-only (backend enforces).
export const mulehunterService = {
    getPhoneInventory: async (fresh?: boolean): Promise<PhoneInventory> => {
        const response = await api.get('/mulehunter/phone-inventory', { params: fresh ? { fresh: true } : undefined });
        return response.data;
    },

    getPhoneInventoryState: async (
        state: string,
        params?: { page?: number; limit?: number },
    ): Promise<PhoneInventoryStatePage> => {
        const response = await api.get(`/mulehunter/phone-inventory/state/${encodeURIComponent(state)}`, { params });
        return response.data;
    },

    stats: async (opts?: { fresh?: boolean; signal?: AbortSignal; timeout?: number }) => {
        const response = await api.get('/mulehunter/stats', {
            params: opts?.fresh ? { fresh: true } : undefined,
            signal: opts?.signal,
            timeout: opts?.timeout ?? 60_000,
        });
        return response.data;
    },

    listIdentifiers: async (
        params?: {
            id_type?: string; source?: string; q?: string;
            min_sources?: number; risk?: string; order?: string;
            limit?: number; offset?: number;
        },
        opts?: { signal?: AbortSignal; timeout?: number },
    ) => {
        const response = await api.get('/mulehunter/identifiers', {
            params,
            signal: opts?.signal,
            timeout: opts?.timeout ?? 15_000,
        });
        return response.data;
    },

    countIdentifiers: async (
        params?: {
            id_type?: string; source?: string; q?: string;
            min_sources?: number; risk?: string;
        },
        opts?: { signal?: AbortSignal; timeout?: number },
    ) => {
        const response = await api.get('/mulehunter/identifiers/count', {
            params,
            signal: opts?.signal,
            timeout: opts?.timeout ?? 60_000,
        });
        return response.data;
    },

    getIdentifier: async (id: number) => {
        const response = await api.get(`/mulehunter/identifiers/${id}`);
        return response.data;
    },

    // admin-only
    runBackfill: async () => {
        const response = await api.post('/mulehunter/backfill');
        return response.data;
    },

    backfillStatus: async () => {
        const response = await api.get('/mulehunter/backfill/status');
        return response.data;
    },

    /** Preview which of a case's identifiers appear in the harvest index (read-only). */
    scanCase: async (caseId: string): Promise<MulehunterCaseScan> => {
        const response = await api.get(`/mulehunter/case/${caseId}/scan`);
        return response.data;
    },

    /** Promote harvest matches + co-occurrence leads into the case graph
     * (scored tier-3 nodes/edges; matched identifiers merge with existing case nodes). */
    promoteCase: async (caseId: string): Promise<MulehunterCasePromote> => {
        const response = await api.post(`/mulehunter/case/${caseId}/promote-to-graph`);
        return response.data;
    },

    /** Promote recent P2P advertisement identifiers into the selected case graph. */
    promoteP2P: async (
        caseId: string,
        params?: { platform?: MulehunterP2PPlatform; limit?: number },
    ): Promise<MulehunterP2PPromote> => {
        const response = await api.post(
            `/mulehunter/case/${caseId}/promote-p2p`,
            undefined,
            { params },
        );
        return response.data;
    },

    /** Promote recent Telegram identifiers into the selected case graph. */
    promoteTelegram: async (
        caseId: string,
        params?: { channel?: string; limit?: number },
    ): Promise<MulehunterTelegramPromote> => {
        const response = await api.post(
            `/mulehunter/case/${caseId}/promote-telegram`,
            undefined,
            { params },
        );
        return response.data;
    },

    /** Batch-screen identifiers against the harvest index (pure read). Max 5000
     *  per call; `type` is inferred server-side when omitted. */
    screen: async (
        identifiers: { value: string; type?: string }[],
        opts?: { signal?: AbortSignal; timeout?: number },
    ): Promise<MulehunterScreenResponse> => {
        const response = await api.post('/mulehunter/screen', { identifiers }, {
            signal: opts?.signal,
            timeout: opts?.timeout ?? 60_000,
        });
        return response.data;
    },
};

// ── /mulehunter/screen response (backend services/mulehunter/service.py
//    screen_batch + _shape_hit + _rail_attribution; vocab in rails_registry.py) ──

/** VPA: rails_registry.attribute_vpa(). All keys always present. */
export interface MulehunterVpaRail {
    valid_format: boolean;
    handle: string | null;
    psp_app: string | null;
    psp_bank: string | null;
    psp_confidence: 'verified' | 'unverified' | null;
    psp_source: string | null;
    merchant_aggregator: string | null;
    merchant_confidence: 'verified' | 'unverified' | null;
    sebi_valid: boolean;
    local_part_phone: string | null;
    reason: string | null;
}

/** UTR: rails_registry.classify_utr(). A single decoded date, not a window. */
export interface MulehunterUtrRail {
    rail: 'upi' | 'imps' | 'neft' | 'rtgs' | 'unknown';
    txn_date: string | null;
    date_confidence: 'secondary_sources_agree' | 'decade_fallback' | null;
    bank_code: string | null;
    reason: string | null;
    /** Only on 12-digit RRNs read as UPI. */
    rail_note?: string;
}

/** IFSC found in mh_ifsc_master. */
export interface MulehunterIfscMasterRail {
    source: 'ifsc_master';
    valid_format: true;
    ifsc: string;
    bank: string | null;
    bank_code: string;
    branch: string | null;
    city: string | null;
    district: string | null;
    state: string | null;
    micr: string | null;
    upi: boolean | null;
    imps: boolean | null;
    neft: boolean | null;
    rtgs: boolean | null;
}

/** IFSC absent from the master (or master not loaded): format facts only. */
export interface MulehunterIfscFormatRail {
    source: 'format_only';
    valid_format: boolean;
    bank_code: string | null;
    branch_code: string | null;
}

export type MulehunterPhoneRegionConfidence = 'explicit' | 'context' | 'context_weak' | 'assumed' | 'unknown';

/** Phone: service._phone_attribution(). */
export interface MulehunterPhoneRail {
    region: string | null;
    confidence: MulehunterPhoneRegionConfidence | null;
    e164: string | null;
    evidence: { rule?: string; region?: string | null; signal?: string; quote?: string }[];
    source: 'index' | 'input';
}

export type MulehunterRailAttribution =
    | MulehunterVpaRail
    | MulehunterUtrRail
    | MulehunterIfscMasterRail
    | MulehunterIfscFormatRail
    | MulehunterPhoneRail;

export interface MulehunterScreenHit {
    input: string;
    type: string;
    canonical: string;
    matched: true;
    tier: 'scored';
    risk_hint: string | null;
    source_count: number;
    cross_source: boolean;
    sources: string[];
    entity_name: string | null;
    entity_category: string | null;
    occurrences: number;
    first_seen: string | null;
    last_seen: string | null;
    origin_count: number;
    corroborated: boolean;
    source_table_count: number;
    collector_tag: string | null;
    days_since_last_seen: number | null;
    freshness: 'active' | 'recent' | 'stale' | 'undated';
    associated_actors: Record<string, number>;
    /** null for identifier types with no rail (wallet, handle, email, ...). */
    rail_attribution: MulehunterRailAttribution | null;
}

export interface MulehunterScreenMiss {
    input: string;
    type: string;
    canonical: string;
    matched: false;
}

export type MulehunterScreenResult = MulehunterScreenHit | MulehunterScreenMiss;

export interface MulehunterScreenResponse {
    results: MulehunterScreenResult[];
    summary: {
        submitted: number;
        resolved: number;
        unresolved: number;
        matched: number;
        match_rate: number;
        cross_source_hits: number;
        corroborated_hits: number;
        actor_associated_hits: number;
        by_freshness: Record<string, number>;
        by_risk: Record<string, number>;
        by_type: Record<string, number>;
        /** Matched VPAs by PSP bank; "unknown" for unattributed/invalid. */
        by_psp_bank: Record<string, number>;
    };
}

export interface MulehunterCaseMatch {
    entity_id: string;
    type: string;
    value: string;
    label?: string | null;
    mh_id: number;
    occurrences: number;
    source_count: number;
    sources: string[];
    cross_source: boolean;
    risk_hint?: string | null;
    entity_name?: string | null;
    entity_category?: string | null;
}

export interface MulehunterCaseScan {
    matches: MulehunterCaseMatch[];
    total: number;
}

export interface MulehunterCasePromote {
    status: string;
    matched: number;
    leads: number;
    edges: number;
}

export type MulehunterP2PPlatform = 'all' | 'binance' | 'okx' | 'bybit' | 'htx';

export interface MulehunterP2PPromote {
    status: 'ok' | 'error';
    platform: MulehunterP2PPlatform;
    ads_scanned: number;
    entities: number;
    edges: number;
    error?: string;
    by_platform?: Record<string, {
        status: 'ok' | 'error';
        platform: string;
        ads_scanned?: number;
        entities?: number;
        edges?: number;
        error?: string;
    }>;
}

export interface MulehunterTelegramPromote {
    status: 'ok' | 'error';
    channel: string;
    messages_scanned: number;
    entities: number;
    edges: number;
    error?: string;
}

export const authService = {
    login: async (formData: FormData) => {
        const response = await api.post('/auth/login', formData, {
            headers: { 'Content-Type': 'multipart/form-data' } // OAuth2 requires form data
        });
        return response.data;
    },

    getMe: async () => {
        const response = await api.get('/auth/me');
        return response.data;
    },

    verify: async () => {
        const response = await api.post('/auth/verify-token');
        return response.data;
    },

    requestOtp: async (purpose: string = 'password_change') => {
        const response = await api.post('/auth/request-otp', { purpose });
        return response.data;
    },

    verifyOtp: async (otp: string, purpose: string = 'password_change') => {
        const response = await api.post('/auth/verify-otp', { otp, purpose });
        return response.data;
    },

    changePassword: async (otp: string, new_password: string) => {
        const response = await api.post('/auth/change-password', { otp, new_password });
        return response.data;
    },

    updateProfile: async (data: { full_name?: string; email?: string; digest_recipients?: string }) => {
        const response = await api.patch('/auth/profile', data);
        return response.data;
    },

    acceptLegal: async (version: string) => {
        const response = await api.post('/auth/accept-legal', { version });
        return response.data;
    },

    exportMyData: async () => {
        const response = await api.get('/auth/me/data-export');
        return response.data;
    },

    erasePersonalData: async (confirmation: string) => {
        const response = await api.post('/auth/erase-personal-data', { confirmation });
        return response.data;
    },
};

export const adminService = {
    seedDemo: async () => {
        const response = await api.post('/admin/seed-demo');
        return response.data;
    },

    triggerIngestion: async () => {
        const response = await api.post('/admin/ingest');
        return response.data;
    },

    /** Clear backend in-memory state (NetworkX graph, caches). Call on Clear Canvas / New Investigation. */
    clearSession: async () => {
        const response = await api.post('/admin/clear-session');
        return response.data;
    },

    // ── 3rd-party provider catalog & usage (super-admin only) ─────────
    /** Static provider registry: chains, capabilities, free-tier quotas, cascade priority. */
    getProviderRegistry: async () => {
        const response = await api.get('/admin/providers/registry');
        return response.data;
    },

    /** Per-provider call counts for the last N days, merged with the in-process buffer. */
    getProviderUsage: async (days: number = 7) => {
        const response = await api.get('/admin/providers/usage', { params: { days } });
        return response.data;
    },

    /** Resolve the provider cascade for a (chain, capability) pair. */
    getProviderCascade: async (chain: string, capability: string) => {
        const response = await api.get('/admin/providers/cascade', { params: { chain, capability } });
        return response.data;
    },

    getProviderCapabilities: async () => {
        const response = await api.get('/admin/providers/capabilities');
        return response.data;
    },

    /** Manually close an open circuit breaker (e.g. after upgrading a paid plan). */
    resetProviderCircuit: async (name: string) => {
        const response = await api.post(`/admin/providers/${encodeURIComponent(name)}/reset-circuit`);
        return response.data;
    },

    /** Current Arkham trial-gating state (master + /transfers switch, credit estimate vs budget). */
    getArkhamControls: async () => {
        const response = await api.get('/admin/providers/arkham/controls');
        return response.data;
    },

    /**
     * Set Arkham runtime gating (super-admin). Keep Arkham OFF for normal
     * investigations to preserve trial credits; flip /transfers ON per demo.
     * Pass clear_*_override to drop a runtime override back to the env default.
     */
    setArkhamControls: async (patch: {
        enabled?: boolean | null;
        transfers_enabled?: boolean | null;
        clear_enabled_override?: boolean;
        clear_transfers_override?: boolean;
        reset_credit_estimate?: boolean;
    }) => {
        const response = await api.post('/admin/providers/arkham/controls', patch);
        return response.data;
    },

    /** SANC02: Freshness coverage across all sanctions sources. */
    getSanctionsCoverage: async () => {
        const response = await api.get('/admin/providers/sanctions/coverage');
        return response.data;
    },

    /**
     * Run a one-shot probe against a provider with a known-labelled fixture
     * address. Confirms API key works, quota not exhausted, response shape
     * matches what we expect.
     */
    testProvider: async (name: string, opts?: { chain?: string; address?: string }) => {
        const params: Record<string, string> = {};
        if (opts?.chain) params.chain = opts.chain;
        if (opts?.address) params.address = opts.address;
        const response = await api.post(
            `/admin/providers/${encodeURIComponent(name)}/test`,
            null,
            { params, timeout: 25_000 },
        );
        return response.data;
    },

    /**
     * Super-admin: cross-user API audit trail. Returns { count, rows: [...] }.
     * since_minutes default 60 (max 10080 = 7 days), limit max 1000.
     */
    getActivityLog: async (params: { limit?: number; since_minutes?: number; user_id?: number; endpoint_like?: string }) => {
        const response = await api.get('/admin/activity-log', { params });
        return response.data;
    },

    // ── Benchmark scoreboard (super-admin only) ───────────────────────
    getBenchRuns: async (days: number = 30, caseLabel?: string) => {
        const response = await api.get<{ runs: BenchRun[]; count: number }>('/admin/bench/runs', {
            params: { days, case_label: caseLabel },
        });
        return response.data;
    },

    getBenchRunFacts: async (runId: number) => {
        const response = await api.get<{ run_id: number; facts: BenchFact[]; count: number }>(
            `/admin/bench/runs/${runId}/facts`
        );
        return response.data;
    },
};

export interface BenchRun {
    id: number;
    night: string;
    case_label: string;
    case_id: string;
    run_ref?: string | null;
    image_tag?: string | null;
    started_at: string;
    finished_at?: string | null;
    seconds?: number | null;
    status: string;
    completed: boolean;
    readiness_score?: number | null;
    readiness_steps?: number | null;
    facts_found?: number | null;
    facts_total?: number | null;
    partial?: number | null;
    missing?: number | null;
    not_exposed?: number | null;
    stale_assertion?: number | null;
    must_not_hits?: number | null;
    error_class?: string | null;
}

export interface BenchFact {
    run_id: number;
    fact_id: string;
    status: string;
}

export const benchService = {
    getRuns: adminService.getBenchRuns,
    getFacts: adminService.getBenchRunFacts,
};



export const userService = {
    getAll: async () => {
        const response = await api.get('/users');
        return response.data;
    },

    getAllWithStats: async () => {
        const response = await api.get('/users/stats');
        return response.data;
    },

    create: async (data: any) => {
        const response = await api.post('/users', data);
        return response.data;
    },

    toggleActive: async (userId: number) => {
        const response = await api.patch(`/users/${userId}/toggle-active`);
        return response.data;
    },

    updateRole: async (userId: number, role: string) => {
        const response = await api.patch(`/users/${userId}/role`, null, { params: { role } });
        return response.data;
    },

    getActivity: async (userId: number, limit: number = 50) => {
        const response = await api.get(`/users/${userId}/activity`, { params: { limit } });
        return response.data;
    },

    updateDigestRecipients: async (userId: number, digestRecipients: string) => {
        const response = await api.patch(`/users/${userId}/digest-recipients`, { digest_recipients: digestRecipients });
        return response.data;
    },

    deleteUser: async (userId: number) => {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
    },

    /** Admin/super-admin: set a new password for another user (min 6 chars). */
    resetPassword: async (userId: number, newPassword: string) => {
        const response = await api.post(`/users/${userId}/reset-password`, { new_password: newPassword });
        return response.data;
    },

    previewDigest: async (days: number = 7) => {
        const response = await api.get('/users/digest/preview', { params: { days } });
        return response.data;
    },

    sendDigest: async (email: string, days: number = 7) => {
        const response = await api.post('/users/digest/send', { email, days });
        return response.data;
    },
};

export const searchService = {
    getHistory: async (limit = 20, searchType?: string) => {
        const params: any = { limit };
        if (searchType && searchType !== 'all') {
            params.search_type = searchType;
        }
        const response = await api.get('/search/history', { params });
        return response.data;
    },

    getRecentlyViewed: async (limit = 10, resourceType?: string) => {
        const response = await api.get('/search/recently-viewed', {
            params: { limit, resource_type: resourceType }
        });
        return response.data;
    },

    getStats: async (days = 7) => {
        const response = await api.get('/search/stats', { params: { days } });
        return response.data;
    },

    recordSearch: async (query: string, searchType: string, chain?: string, resultCount?: number, metadata?: any) => {
        const response = await api.post('/search/history', {
            search_query: query,
            search_type: searchType,
            chain,
            results_count: resultCount,
            metadata
        });
        return response.data;
    },

    recordView: async (resourceType: string, resourceId: string, resourceData?: any) => {
        const response = await api.post('/search/views', {
            resource_type: resourceType,
            resource_id: resourceId,
            resource_data: resourceData
        });
        return response.data;
    }
};



export const investigationService = {
    listByCase: async (caseId: string, includeGraphState = true) => {
        const response = await api.get(`/cases/${caseId}/investigations`, {
            params: { include_graph_state: includeGraphState }
        });
        return response.data;
    },

    /**
     * Save a new investigation snapshot with enriched metadata.
     * graphState should include:
     *   - elements: Cytoscape nodes/edges
     *   - screenshot: base64 data URI of the workbench canvas
     *   - intelligence_summary: AI narrative text (if generated)
     *   - node_intelligence: { [address]: { risk_score, entity_name, ... } }
     *   - pattern_scans: { typologies: [...] }
     *   - action_log: string[] of investigator actions taken
     */
    create: async (
        caseId: string,
        name: string,
        graphState: {
            elements?: any[];
            layout?: any;
            screenshot?: string;
            intelligence_summary?: string;
            node_intelligence?: Record<string, any>;
            pattern_scans?: any;
            action_log?: string[];
            investigation_type?: string;
            [key: string]: any;
        },
        notes?: string,
        rootAddress?: string,
        rootChain?: string,
        investigationType?: string
    ) => {
        const response = await api.post(`/cases/${caseId}/investigations`, {
            case_id: caseId,
            name,
            graph_state: graphState,
            notes,
            root_address: rootAddress,
            root_chain: rootChain,
            investigation_type: investigationType || graphState?.investigation_type || 'crypto'
        });
        return response.data;
    },

    get: async (id: string, caseId: string) => {
        // Saved investigation snapshots can be multi-MB (graph_state with many
        // nodes/edges). The global 30s timeout aborts the load of a large
        // snapshot, leaving the canvas blank on reopen. Override to 120s — the
        // save path now strips the data-layer tx bloat, but legacy heavy
        // snapshots still need the headroom. Mirrors addressService.trace.
        const response = await api.get(`/cases/${caseId}/investigations/${id}`, { timeout: 120_000 });
        return response.data;
    },

    /**
     * Update investigation name, notes, or graph_state.
     */
    update: async (caseId: string, invId: string, data: { name?: string; notes?: string; graph_state?: any }) => {
        const response = await api.patch(`/cases/${caseId}/investigations/${invId}`, data);
        return response.data;
    },

    createReportArtifact: async (
        caseId: string,
        investigationId: string,
        payload: {
            title: string;
            html_content: string;
            report_type?: string;
            report_format?: string;
            metadata?: Record<string, any>;
        }
    ) => {
        const response = await api.post(
            `/cases/${caseId}/investigations/${investigationId}/reports`,
            payload
        );
        return response.data;
    },

    listReportArtifacts: async (caseId: string, investigationId: string) => {
        const response = await api.get(`/cases/${caseId}/investigations/${investigationId}/reports`);
        return response.data;
    },

    getReportArtifact: async (caseId: string, investigationId: string, reportId: string) => {
        const response = await api.get(`/cases/${caseId}/investigations/${investigationId}/reports/${reportId}`);
        return response.data;
    }
};


export interface NoticeTemplate {
    key: string;
    file: string;
    label: string;
    description: string;
    jurisdiction: string | null;
    exchange: string | null;
    is_default: boolean;
}

/** Routed-subpoena recommendation for an off-ramp finding (exchange-typing). */
export interface NoticeSuggestion {
    target_address: string;
    chain: string;
    exchange_name: string;
    jurisdiction_bucket: 'india_direct' | 'india_via_partner' | 'mlat_required' | 'non_actionable';
    jurisdiction_label: string;
    priority: number;
    actionable: boolean;
    recommended_template_key: string | null;   // 'section91' | 'section94' | null
    basis: string;
    notes: string;
}

export interface BatchNoticeResultItem {
    exchange_name?: string;
    address?: string;
    chain?: string;
    status: 'success' | 'failed' | 'skipped_duplicate' | 'skipped' | string;
    evidence_id?: number | null;
    error?: string | null;
    batch_id?: string;
    retryable?: boolean;
    pdf_base64?: string;
    filename?: string;
    skip_reason?: string;
    duplicate_of?: string;
    related_addresses?: string[];
    template_key?: string;
}

export interface BatchNoticeResponse {
    batch_id: string;
    results: BatchNoticeResultItem[];
    summary: {
        success?: number;
        failed?: number;
        skipped_duplicate?: number;
        skipped?: number;
        total?: number;
        [k: string]: number | undefined;
    };
}

export const legalService = {
    /** List notice templates with the per-exchange default flagged. */
    listTemplates: async (exchange?: string): Promise<{
        exchange: string | null;
        jurisdiction: string | null;
        templates: NoticeTemplate[];
    }> => {
        const response = await api.get('/legal/notice-templates', {
            params: exchange ? { exchange } : undefined,
        });
        return response.data;
    },

    /** Recommend a routed subpoena for an off-ramp finding: given the exchange +
     *  the depositing address + how the exchange wallet was identified, returns the
     *  jurisdiction bucket, recommended template, and a court-ready basis line. */
    suggestNotice: async (data: {
        exchange_name: string;
        chain: string;
        address: string;
        wallet_role?: string | null;
        attribution_method?: string | null;
        attribution_tier?: string | null;
        is_sanctioned?: boolean;
    }): Promise<NoticeSuggestion> => {
        const response = await api.post('/legal/suggest-notice', data);
        return response.data;   // R3: single unwrap — never .then(r => r.data)
    },

    /** Generate a notice PDF. Returns both the blob (for download) and the
     *  X-Notice-Evidence-Id header value when persisted (case_id was set). */
    generateNotice: async (data: any): Promise<{ blob: Blob; evidenceId: number | null }> => {
        const response = await api.post('/legal/generate-notice', data, {
            responseType: 'blob',
        });
        const headerVal = response.headers['x-notice-evidence-id'] || response.headers['X-Notice-Evidence-Id'];
        const evidenceId = headerVal ? Number(headerVal) : null;
        return { blob: response.data as Blob, evidenceId: Number.isFinite(evidenceId) ? evidenceId : null };
    },

    /** Multi-exchange batch: one notice per unique exchange; partial failure + retry. */
    batchGenerateNotices: async (data: {
        notices: Array<{
            exchange_name: string;
            chain: string;
            address: string;
            template_key?: string | null;
            notice_type?: string | null;
            wallet_role?: string | null;
            attribution_method?: string | null;
            attribution_tier?: string | null;
            is_offramp_point?: boolean;
            related_addresses?: string[];
        }>;
        case_context?: Record<string, string>;
        case_id?: string;
        investigation_id?: string;
        omit_pdf_bytes?: boolean;
    }): Promise<BatchNoticeResponse> => {
        const response = await api.post('/legal/batch-generate-notices', data);
        return response.data; // R3: single unwrap
    },

    /** List drafted notices for a case. */
    listNotices: async (caseId: string) => {
        const response = await api.get('/legal/notices', { params: { case_id: caseId } });
        return response.data;
    },

    /** Patch lifecycle fields (status / sent_at / response_received_at / response_summary). */
    updateNotice: async (
        evidenceId: number,
        caseId: string,
        update: {
            status?: 'drafted' | 'sent' | 'responded' | 'escalated';
            sent_at?: string;
            response_due_at?: string;
            response_received_at?: string;
            response_summary?: string;
        },
    ) => {
        const response = await api.patch(`/legal/notices/${evidenceId}`, update, {
            params: { case_id: caseId },
        });
        return response.data;
    },
};

export const riskService = {
    getConfig: async () => {
        const response = await api.get('/risk/config');
        return response.data;
    },

    updateConfig: async (config: any) => {
        const response = await api.post('/risk/config', config);
        return response.data;
    },

    /** Built-in rules (tier cut-offs, P2P thresholds, signal_weights all 1.0). */
    getDefaults: async () => {
        const response = await api.get('/risk/config/defaults');
        return response.data;
    },

    /** Restore the built-in rules on the server; returns { status, rules }. */
    resetConfig: async () => {
        const response = await api.post('/risk/config/reset');
        return response.data;
    },
};

export const analyticsService = {
    scanPatterns: async (graphData: { nodes: any[], edges: any[] }, chain?: string) => {
        const response = await api.post('/analytics/pattern-scan', { ...graphData, chain });
        return response.data;
    },
    clusterAddress: async (chain: string, address: string, limit = 100) => {
        const response = await api.post(`/analytics/cluster/${chain}/${address}?limit=${limit}`);
        return response.data;
    },
};

export const investigationAnalysisService = {
    prefetchRoot: async (chain: string, address: string, limit = 200) => {
        const response = await api.post('/investigation/prefetch-root', { chain, address, limit });
        return response.data;
    },
    backwardTrace: async (chain: string, address: string, maxHops = 3, targetEntityTypes?: string[]) => {
        // Backend BFS has 120s wall-clock budget; generous frontend margin so
        // backend always returns before Axios gives up. User sees progress in card.
        const response = await api.post('/investigation/backward-trace', { chain, address, max_hops: maxHops, target_entity_types: targetEntityTypes }, { timeout: 600000 });
        return response.data;
    },
    forwardTrace: async (chain: string, address: string, maxHops = 3, targetEntityTypes?: string[]) => {
        const response = await api.post('/investigation/forward-trace', { chain, address, max_hops: maxHops, target_entity_types: targetEntityTypes }, { timeout: 600000 });
        return response.data;
    },
    /**
     * SSE streaming backward trace — keeps Railway proxy alive with periodic
     * heartbeat events. Falls back to POST if EventSource fails immediately.
     */
    backwardTraceStream: (
        chain: string,
        address: string,
        maxHops: number = 4,
        onProgress?: (event: { type: string; data: any }) => void,
    ): { promise: Promise<any>; close: () => void } => {
        let es: EventSource | null = null;
        let closed = false;
        let settled = false; // Guard against double-settle from error event + onerror race
        const promise = new Promise<any>((resolve, reject) => {
            const token = localStorage.getItem('token') || '';
            const params = new URLSearchParams({
                chain,
                address,
                max_hops: String(maxHops),
                token,
            });
            const url = `${API_BASE_URL}/investigation/backward-trace/stream?${params}`;
            let receivedEvent = false;

            es = new EventSource(url);

            es.addEventListener('phase', (e: MessageEvent) => {
                receivedEvent = true;
                try { onProgress?.({ type: 'phase', data: JSON.parse(e.data) }); } catch {}
            });
            es.addEventListener('hop', (e: MessageEvent) => {
                receivedEvent = true;
                try { onProgress?.({ type: 'hop', data: JSON.parse(e.data) }); } catch {}
            });
            es.addEventListener('heartbeat', () => {
                receivedEvent = true;
            });
            es.addEventListener('complete', (e: MessageEvent) => {
                if (settled) return;
                settled = true;
                closed = true;
                es?.close();
                try { resolve(JSON.parse(e.data)); } catch (err) { reject(err); }
            });
            // Named 'error' SSE event from backend (not connection error)
            es.addEventListener('error', (e: MessageEvent) => {
                if (settled) return;
                settled = true;
                closed = true;
                es?.close();
                try {
                    const data = JSON.parse(e.data);
                    reject(new Error(data.error || 'Stream error'));
                } catch {
                    reject(new Error('Stream error'));
                }
            });
            // Connection-level error (network drop, CORS, etc.)
            // EventSource auto-reconnects by default — close() prevents this.
            es.onerror = () => {
                if (closed || settled) return;
                settled = true;
                closed = true;
                es?.close();
                if (!receivedEvent) {
                    // EventSource failed before any events — fall back to POST
                    investigationAnalysisService.backwardTrace(chain, address, maxHops)
                        .then(resolve)
                        .catch(reject);
                } else {
                    reject(new Error('SSE connection lost'));
                }
            };
        });
        return {
            promise,
            close: () => { closed = true; es?.close(); },
        };
    },
    /**
     * SSE streaming forward trace — keeps Railway proxy alive with periodic
     * heartbeat events. Falls back to POST if EventSource fails immediately.
     */
    forwardTraceStream: (
        chain: string,
        address: string,
        maxHops: number = 4,
        onProgress?: (event: { type: string; data: any }) => void,
    ): { promise: Promise<any>; close: () => void } => {
        let es: EventSource | null = null;
        let closed = false;
        let settled = false;
        const promise = new Promise<any>((resolve, reject) => {
            const token = localStorage.getItem('token') || '';
            const params = new URLSearchParams({
                chain,
                address,
                max_hops: String(maxHops),
                token,
            });
            const url = `${API_BASE_URL}/investigation/forward-trace/stream?${params}`;
            let receivedEvent = false;

            es = new EventSource(url);

            es.addEventListener('phase', (e: MessageEvent) => {
                receivedEvent = true;
                try { onProgress?.({ type: 'phase', data: JSON.parse(e.data) }); } catch {}
            });
            es.addEventListener('hop', (e: MessageEvent) => {
                receivedEvent = true;
                try { onProgress?.({ type: 'hop', data: JSON.parse(e.data) }); } catch {}
            });
            es.addEventListener('heartbeat', () => {
                receivedEvent = true;
            });
            es.addEventListener('complete', (e: MessageEvent) => {
                if (settled) return;
                settled = true;
                closed = true;
                es?.close();
                try { resolve(JSON.parse(e.data)); } catch (err) { reject(err); }
            });
            es.addEventListener('error', (e: MessageEvent) => {
                if (settled) return;
                settled = true;
                closed = true;
                es?.close();
                try {
                    const data = JSON.parse(e.data);
                    reject(new Error(data.error || 'Stream error'));
                } catch {
                    reject(new Error('Stream error'));
                }
            });
            es.onerror = () => {
                if (closed || settled) return;
                settled = true;
                closed = true;
                es?.close();
                if (!receivedEvent) {
                    investigationAnalysisService.forwardTrace(chain, address, maxHops)
                        .then(resolve)
                        .catch(reject);
                } else {
                    reject(new Error('SSE connection lost'));
                }
            };
        });
        return {
            promise,
            close: () => { closed = true; es?.close(); },
        };
    },
    /**
     * Unified SSE investigation stream — runs all 7 investigation steps via
     * a single EventSource connection. Emits typed events (step_start,
     * step_result, progress, node_discovered, edge_discovered, heartbeat,
     * complete, error) so the frontend can render live progress and push
     * discovered nodes/edges to the canvas in real-time.
     *
     * Falls back to legacy POST-per-section flow if EventSource fails
     * immediately (onEvent receives 'connection_error').
     */
    investigationStream: (
        chain: string,
        address: string,
        options: {
            backwardHops?: number;
            exposureHops?: number;
            forwardHops?: number;
            /** Case the run belongs to: the backend promotes the completed run to its spine. */
            caseId?: string | null;
            /** Saved investigation the run belongs to (provenance label on the promotion). */
            investigationId?: string | null;
        },
        onEvent: (event: { type: string; data: any }) => void,
    ): { close: () => void } => {
        const token = localStorage.getItem('token') || '';
        const params = new URLSearchParams({
            chain,
            address,
            backward_hops: String(options.backwardHops ?? 4),
            exposure_hops: String(options.exposureHops ?? 3),
            forward_hops: String(options.forwardHops ?? 4),
            token,
        });
        if (options.caseId) {
            params.set('case_id', options.caseId);
            if (options.investigationId) params.set('investigation_id', options.investigationId);
        }
        const url = `${API_BASE_URL}/investigation/run/stream?${params}`;

        let closed = false;
        let receivedEvent = false;
        const es = new EventSource(url);

        const EVENT_TYPES = [
            'step_start', 'step_result', 'progress',
            'node_discovered', 'edge_discovered', 'decision',
            'heartbeat', 'complete', 'error',
            'phase', 'hop',
        ];

        for (const type of EVENT_TYPES) {
            es.addEventListener(type, (e: MessageEvent) => {
                receivedEvent = true;
                if (closed) return;
                try {
                    onEvent({ type, data: JSON.parse(e.data) });
                } catch { /* malformed JSON — skip */ }
            });
        }

        // Connection-level error (network drop, CORS, etc.)
        es.onerror = () => {
            if (closed) return;
            closed = true;
            es.close();
            // If no events were received, signal the caller to fall back
            onEvent({
                type: 'connection_error',
                data: { message: receivedEvent ? 'SSE connection lost' : 'SSE connection failed', receivedEvent },
            });
        };

        return {
            close: () => {
                closed = true;
                es.close();
            },
        };
    },
    counterpartyTriage: async (chain: string, address: string, topN = 50, minVolume = 0, txLimit = 1000) => {
        const response = await api.post('/investigation/counterparty-triage', { chain, address, top_n: topN, min_volume: minVolume, tx_limit: txLimit }, { timeout: 50000 });
        return response.data;
    },
    multiHopExposure: async (chain: string, address: string, maxHops = 3, limitPerHop = 50) => {
        const response = await api.post('/investigation/multi-hop-exposure', { chain, address, max_hops: maxHops, limit_per_hop: limitPerHop }, { timeout: 130000 });
        return response.data;
    },
    adaptiveTransactions: async (chain: string, address: string, initialBatch = 100, maxTotal = 2000) => {
        const response = await api.post('/investigation/adaptive-transactions', { chain, address, initial_batch: initialBatch, max_total: maxTotal }, { timeout: 50000 });
        return response.data;
    },
    velocity: async (chain: string, address: string, limit = 500) => {
        const response = await api.post('/investigation/velocity', { chain, address, limit });
        return response.data;
    },
    detectExchange: async (chain: string, address: string) => {
        const response = await api.post('/investigation/detect-exchange', { chain, address, fetch_transactions: true, persist: true });
        return response.data;
    },
    privacyDeepCheck: async (txHash: string, privacyChain: string, timestamp: string, windowMinutes = 30, canvasAddresses?: Array<{address: string; chain: string}>) => {
        const response = await api.post('/investigation/privacy-deep-check', {
            tx_hash: txHash,
            privacy_chain: privacyChain,
            timestamp,
            window_minutes: windowMinutes,
            ...(canvasAddresses?.length ? { canvas_addresses: canvasAddresses } : {}),
        });
        return response.data;
    },
    ringElimination: async (txHash: string, investigationId?: string, additionalKnownHashes?: string[]) => {
        const response = await api.post('/investigation/ring-elimination', {
            tx_hash: txHash,
            ...(investigationId ? { investigation_id: investigationId } : {}),
            ...(additionalKnownHashes?.length ? { additional_known_hashes: additionalKnownHashes } : {}),
        });
        return response.data;
    },
    /**
     * Shared-funder corridor review + AI playbook draft. On AI failure the
     * backend returns `status: "unavailable"`, `playbook: null`, `ai_error`;
     * on success it adds `ai_meta`. `case_id` is sent when a case is open.
     */
    analyzeSyndicateCorridor: async (chain: string, address: string, opts?: { caseId?: string | null }) => {
        const response = await api.post('/investigation/syndicate-corridor', {
            chain,
            address,
            ...(opts?.caseId ? { case_id: opts.caseId } : {}),
        }, { timeout: 120000 });
        return response.data;
    },
    /**
     * Bidirectional AI-assisted trace. The backend requires `case_id` (ownership
     * check), clamps depth to 1-3 and returns 403 when the feature flag is off.
     * Edges carry `selection_method`, `tx_hash`, `value`, `token_symbol`; the
     * response carries `ai_meta`.
     */
    aiTrace: async (
        chain: string,
        address: string,
        opts: { caseId: string; maxForwardDepth?: number; maxBackwardDepth?: number },
    ) => {
        const clampDepth = (d: number | undefined) => Math.min(3, Math.max(1, Math.round(Number(d) || 2)));
        const response = await api.post('/investigation/ai-trace', {
            chain,
            address,
            case_id: opts.caseId,
            max_forward_depth: clampDepth(opts.maxForwardDepth),
            max_backward_depth: clampDepth(opts.maxBackwardDepth),
        }, { timeout: 120000 });
        return response.data;
    },
    importExchangeCsv: async (file: File, exchange?: string, investigationId?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams();
        if (exchange) params.set('exchange', exchange);
        if (investigationId) params.set('investigation_id', investigationId);
        const response = await api.post(`/investigation/import-exchange-csv?${params.toString()}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const identityService = {
    /** DB-only lookup: wallet → phones/emails → breach data → names */
    getWalletIdentity: async (chain: string, address: string) => {
        const response = await api.get(`/identity/${chain}/${address}`);
        return response.data;
    },
    /** On-demand enrichment for a specific phone/email (HIBP + local DB) */
    enrich: async (identifier: string, type: string = 'auto') => {
        const response = await api.post('/identity/enrich', { identifier, type });
        return response.data;
    },
    /** Cached OSINT pivot for a phone (identity_pivots). Free; never calls a provider. */
    getByPhone: async (phone: string) => {
        const response = await api.get(`/identity/by-phone/${encodeURIComponent(phone)}`);
        return response.data;
    },
    /** LIVE paid OSINT lookup for one phone (Outris). Result is cached server-side. */
    enrichPhone: async (phone: string, linked?: { wallet?: string | null; upi?: string | null }) => {
        const response = await api.post('/identity/enrich-phone', {
            phone,
            linked_wallet: linked?.wallet || undefined,
            linked_upi: linked?.upi || undefined,
        });
        return response.data;
    }
};

export const entityEnrichmentService = {
    /** Kick off background entity enrichment for unknown addresses.
     *  forceLive: when true, every provider bypasses its in-memory known-label
     *  filter AND the negative-cache. Use for LE Section-91/94 prep on terminal
     *  exchanges + top counterparties. Capped at 50 addresses. */
    startEnrichment: async (
        addresses: Array<{address: string; chain: string}>,
        opts?: { forceLive?: boolean },
    ) => {
        const body: any = { addresses };
        if (opts?.forceLive) body.force_live = true;
        const response = await api.post('/addresses/entity-enrichment/start', body);
        return response.data as {
            job_id: string;
            total: number;
            providers: string[];
            force_live?: boolean;
        };
    },
    /** Poll enrichment job status */
    getStatus: async (jobId: string) => {
        const response = await api.get(`/addresses/entity-enrichment/${jobId}`);
        return response.data as {
            status: 'running' | 'complete' | 'error';
            total: number;
            found: number;
            current_provider: string | null;
            providers_completed: string[];
            results: Record<string, { name: string; category: string; source: string }>;
            error?: string;
        };
    },
};

export const systemService = {
    getSupportedChains: async () => {
        const response = await api.get('/system/supported-chains');
        return response.data;
    },
    getUpstreamProviders: async () => {
        const response = await api.get('/system/upstream-providers');
        return response.data;
    },
    getConnections: async () => {
        const response = await api.get('/system/connections');
        return response.data;
    },
    getDetectionThresholds: async () => {
        const response = await api.get('/settings/detection-thresholds');
        return response.data;
    },
    toggleCrystalAuto: async (enabled: boolean) => {
        const response = await api.post('/system/crystal/toggle-auto', { enabled });
        return response.data;
    },
};

// FlowGuard fiat off-ramp — L1 registry match + L2 UTR decode/stitch. Backend: app/api/v1/fiat.py.
// R3: every method returns response.data — never double-unwrap when consuming.
export const fiatService = {
    // L1 — name the off-ramp behind a beneficiary VPA / account (deterministic-first, no chain query).
    // Day 23 — optional caseId promotes COUNTERPARTY onto case_entities/case_edges.
    counterpartyMatch: async (
        payload: {
            vpa?: string;
            account?: string;
            ifsc?: string;
            name?: string;
            phone?: string;
            narration?: string;
        },
        caseId?: string,
    ) => {
        const response = await api.post('/fiat/counterparty-match', {
            ...payload,
            ...(caseId ? { case_id: caseId } : {}),
        });
        return response.data;
    },
    // T3 — QR pn= → bank verify (opt-in) → registry enrich; also returns L1 match.
    resolvePayee: async (payload: {
        upi_uri?: string;
        vpa?: string;
        account?: string;
        ifsc?: string;
        name?: string;
        enrich_registry?: boolean;
    }) => {
        const response = await api.post('/fiat/resolve-payee', payload);
        return response.data;
    },
    // L2 — validate/decode a UTR/RRN; pass the statement date for a tamper (date-mismatch) check.
    decodeUtr: async (utr: string, lineDate?: string) => {
        const response = await api.get(`/fiat/utr/${encodeURIComponent(utr)}/decode`, {
            params: lineDate ? { line_date: lineDate } : undefined,
        });
        return response.data;
    },
    // L2 — stitch Dr/Cr statement legs into deterministic PAID edges by shared UTR.
    // Day 22 — optional caseId promotes TRACE edges onto case_entities/case_edges.
    stitch: async (events: any[], caseId?: string) => {
        const response = await api.post('/fiat/stitch', {
            events,
            ...(caseId ? { case_id: caseId } : {}),
        });
        return response.data;
    },
    // Phase 3 — clean-vs-tainted triage (FIFO from NCRP tainted UTRs).
    triage: async (payload: {
        events: any[];
        tainted_utrs: string[];
        account_ref?: string;
        closing_balance?: number;
        method?: string;
    }) => {
        const response = await api.post('/fiat/triage', payload);
        return response.data;
    },
    // Phase 3 — source-of-funds / P2P-crypto-sale EDD flag.
    sofEdd: async (payload: {
        events: any[];
        account_ref?: string;
        fan_in_min?: number;
        round_ratio?: number;
        rapid_hours?: number;
        rapid_turnover?: number;
    }) => {
        const response = await api.post('/fiat/sof-edd', payload);
        return response.data;
    },
    // Admin — insert/refresh one VASP-OTC registry node (the moat data).
    upsertRegistryNode: async (payload: any) => {
        const response = await api.post('/fiat/registry', payload);
        return response.data;
    },
};

// Static demo cache held in memory after first fetch from /demo_cache.json
let _staticDemoCache: Record<string, any> | null = null;
async function getStaticDemoCache(): Promise<Record<string, any>> {
    if (_staticDemoCache) return _staticDemoCache;
    try {
        const resp = await fetch('/demo_cache.json');
        if (resp.ok) {
            _staticDemoCache = await resp.json();
            return _staticDemoCache!;
        }
    } catch { /* static file not available */ }
    return {};
}

export const demoService = {
    /** List all cached demo entries (without full elements) */
    listCached: async (): Promise<Array<{ demo_id: string; chain?: string; address?: string; element_count?: number; source_description?: string }>> => {
        try {
            const response = await api.get('/demo/cached');
            const data = response.data;
            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.items)) return data.items;
            return [];
        } catch {
            // Fallback to static JSON keys
            const cache = await getStaticDemoCache();
            return Object.entries(cache).map(([id, v]: [string, any]) => ({
                demo_id: id,
                chain: v.chain,
                address: v.address,
                element_count: v.elements?.length || 0,
                source_description: v.source_description,
            }));
        }
    },
    /** Get cached real-data elements for a specific demo */
    getCached: async (demoId: string) => {
        try {
            const response = await api.get(`/demo/cached/${demoId}`);
            return response.data;
        } catch {
            // Fallback to static JSON
            const cache = await getStaticDemoCache();
            const entry = cache[demoId];
            if (entry) return { ...entry, element_count: entry.elements?.length || 0 };
            throw new Error(`Demo ${demoId} not found in cache`);
        }
    },
    /** Store pre-fetched real data for a demo (admin) */
    populateCache: async (demoId: string, data: { chain: string; address: string; elements: any[]; transactions?: any[]; source_description?: string; metadata?: any }) => {
        const response = await api.post(`/demo/cached/${demoId}`, { demo_id: demoId, ...data });
        return response.data;
    },
    /** Delete cached demo data */
    deleteCache: async (demoId: string) => {
        const response = await api.delete(`/demo/cached/${demoId}`);
        return response.data;
    }
};

export const transactionService = {
    /**
     * Get transaction details by hash
     */
    getTransaction: async (chain: string, txHash: string) => {
        const response = await api.get(`/transactions/${chain}/${txHash}`);
        return response.data;
    },

    /**
     * Get transaction graph (nodes + edges) for visualization
     */
    getTransactionGraph: async (chain: string, txHash: string) => {
        const response = await api.get(`/transactions/${chain}/${txHash}/graph`);
        return response.data;
    },

    /**
     * Get transaction context (nearby transactions for both parties)
     */
    getTransactionContext: async (chain: string, txHash: string, before = 5, after = 5) => {
        const response = await api.get(`/transactions/${chain}/${txHash}/context`, {
            params: { before, after }
        });
        return response.data;
    },

    /**
     * Find all transactions between two addresses
     */
    getTransactionsBetween: async (chain: string, address1: string, address2: string, limit = 50) => {
        const response = await api.get(`/transactions/${chain}/between`, {
            params: { address1, address2, limit }
        });
        return response.data;
    },

    /**
     * Search for transaction by hash (auto-detects chain)
     */
    searchTransaction: async (txHash: string, chain?: string) => {
        const response = await api.get('/transactions/search', {
            params: { q: txHash, chain }
        });
        return response.data;
    },

    /**
     * Detect chain from transaction hash format
     * Note: BTC and TRX both use 64-hex format - cannot distinguish!
     * Use searchTransaction() for reliable cross-chain detection.
     */
    detectChainFromHash: (txHash: string): string | null => {
        if (!txHash) return null;
        txHash = txHash.trim();

        // EVM chains (ETH, MATIC, BNB) - 0x prefix + 64 hex chars
        if (txHash.startsWith('0x') && txHash.length === 66) {
            return 'ETH';
        }

        // Tron or Bitcoin - 64 hex chars (no 0x) - CANNOT DISTINGUISH!
        // Return null to force using search API which tries both
        if (txHash.length === 64 && /^[0-9a-fA-F]+$/.test(txHash)) {
            return null;  // Ambiguous - must use search API
        }

        // Solana - Base58, 43-88 chars
        if (txHash.length >= 43 && txHash.length <= 88 && !txHash.startsWith('0x')) {
            return 'SOL';
        }

        return null;
    },

    /**
     * Check if a string looks like a transaction hash
     */
    isTransactionHash: (input: string): boolean => {
        if (!input) return false;
        input = input.trim();

        // EVM transaction hash
        if (input.startsWith('0x') && input.length === 66 && /^0x[0-9a-fA-F]+$/.test(input)) {
            return true;
        }

        // Tron/BTC hash - 64 hex chars
        if (input.length === 64 && /^[0-9a-fA-F]+$/.test(input)) {
            return true;
        }

        // Solana signature (base58, 43-88 chars; use >= 45 to avoid overlap with SOL addresses which are 32-44)
        if (input.length >= 45 && input.length <= 88 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(input)) {
            return true;
        }

        // TON transaction hash (base64 with +, /, or = characters)
        if (input.length >= 40 && input.length <= 50 && /[+/=]/.test(input) && /^[A-Za-z0-9+/=]+$/.test(input)) {
            return true;
        }

        return false;
    },

    /**
     * Get blockchain explorer URL for a transaction
     */
    getExplorerUrl: (chain: string, txHash: string): string => {
        const explorers: Record<string, string> = {
            'ETH': `https://etherscan.io/tx/${txHash}`,
            'TRX': `https://tronscan.org/#/transaction/${txHash}`,
            'BTC': `https://blockstream.info/tx/${txHash}`,
            'LTC': `https://litecoinspace.org/tx/${txHash}`,
            'DOGE': `https://blockchair.com/dogecoin/transaction/${txHash}`,
            'BCH': `https://blockchair.com/bitcoin-cash/transaction/${txHash}`,
            'MATIC': `https://polygonscan.com/tx/${txHash}`,
            'BNB': `https://bscscan.com/tx/${txHash}`,
            'OPBNB': `https://opbnb.bscscan.com/tx/${txHash}`,
            'SOL': `https://solscan.io/tx/${txHash}`,
            'ARB': `https://arbiscan.io/tx/${txHash}`,
            'BASE': `https://basescan.org/tx/${txHash}`,
            'OP': `https://optimistic.etherscan.io/tx/${txHash}`,
        };
        return explorers[chain] || `https://etherscan.io/tx/${txHash}`;
    }
};

export const forensicService = {
    validate: async (caseId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/forensic/${caseId}/validate`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    upload: async (caseId: string, file: File, investigationId?: string, notes?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = new URLSearchParams();
        if (investigationId) params.set('investigation_id', investigationId);
        if (notes) params.set('notes', notes);
        const response = await api.post(`/forensic/${caseId}/upload?${params.toString()}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    deleteRecord: async (caseId: string, recordId: string) => {
        const response = await api.delete(`/forensic/${caseId}/records/${recordId}`);
        return response.data as { success: boolean; record_id: string; file_name: string; links_deleted: number };
    },

    getRecords: async (caseId: string) => {
        const response = await api.get(`/forensic/${caseId}/records`);
        return response.data;
    },

    getCanvasOverlay: async (caseId: string): Promise<ForensicOverlay> => {
        const response = await api.get(`/forensic/${caseId}/canvas-overlay`);
        return response.data;
    },
};

// ---- Instagram OSINT (Tier-4) ----
export interface InstagramArtifact {
    artifact_type: string;
    value: string;
    chain: string | null;
    valid: boolean;
    rejected_reason: string | null;
    source_platform: string;
    source_url: string | null;
    source_surface: string;
    source_media_index: number | null;
    offset: number | null;
    ocr_confidence: number | null;
    source_language: string | null;
    captured_at: string | null;
    content_hash: string | null;
    tier: number;
    raw_context: string | null;
    extractor: string | null;
}

export interface InstagramMediaResult {
    media_index: number;
    filename: string;
    content_hash: string;
    caption: string | null;
    source_url: string | null;
    ocr: {
        engine: string;
        ocr_text_original: string;
        detected_language: string | null;
        ocr_text_english: string | null;
        confidence: number | null;
        qr_payloads: string[];
        translator: string | null;
        notes: string[];
    };
    consolidated_text: string;
    artifacts: InstagramArtifact[];
}

export interface InstagramOsintRunResult {
    run_id: string | null;
    case_id: string;
    suspect_handle: string;
    committed: boolean;
    media: InstagramMediaResult[];
    artifacts: InstagramArtifact[];
    counts: Record<string, number>;
    by_surface: Record<string, number>;
    manifest_ndjson: string | null;
    manifest_signature: string | null;
    warnings: string[];
}

export const instagramOsintService = {
    /** Phase 1: OCR + QR + extraction over investigator-supplied images. */
    suppliedMedia: async (
        caseId: string,
        suspectHandle: string,
        files: File[],
        opts?: {
            captions?: (string | undefined)[];
            sourceUrls?: (string | undefined)[];
            commit?: boolean;
            examiner?: string;
            // Phase A — profile / bio fields (any subset; images optional when present)
            bio?: string;
            externalUrl?: string;
            fullName?: string;
            publicEmail?: string;
            publicPhone?: string;
            seed?: string;
            signal?: AbortSignal;
            timeout?: number;
        },
    ): Promise<InstagramOsintRunResult> => {
        const formData = new FormData();
        formData.append('suspect_handle', suspectHandle);
        files.forEach((f) => formData.append('files', f));
        (opts?.captions || []).forEach((c) => formData.append('captions', c ?? ''));
        (opts?.sourceUrls || []).forEach((u) => formData.append('source_urls', u ?? ''));
        if (opts?.bio) formData.append('bio', opts.bio);
        if (opts?.externalUrl) formData.append('external_url', opts.externalUrl);
        if (opts?.fullName) formData.append('full_name', opts.fullName);
        if (opts?.publicEmail) formData.append('public_email', opts.publicEmail);
        if (opts?.publicPhone) formData.append('public_phone', opts.publicPhone);
        if (opts?.seed) formData.append('seed', opts.seed);
        formData.append('commit', String(opts?.commit ?? true));
        if (opts?.examiner) formData.append('examiner', opts.examiner);
        const response = await api.post(`/osint/instagram/${caseId}/supplied-media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: opts?.signal,
            timeout: opts?.timeout ?? 120_000,
        });
        return response.data;
    },

    /** Phase 3: acquire the suspect's own public posts via a pluggable backend. */
    fetchFromHandle: async (
        caseId: string,
        suspectHandle: string,
        opts?: {
            backend?: string;
            limit?: number;
            commit?: boolean;
            examiner?: string;
            legalBasis?: string;
            signal?: AbortSignal;
            timeout?: number;
        },
    ): Promise<InstagramOsintRunResult> => {
        const formData = new FormData();
        formData.append('suspect_handle', suspectHandle);
        if (opts?.backend) formData.append('backend', opts.backend);
        formData.append('limit', String(opts?.limit ?? 12));
        formData.append('commit', String(opts?.commit ?? true));
        if (opts?.examiner) formData.append('examiner', opts.examiner);
        if (opts?.legalBasis) formData.append('legal_basis', opts.legalBasis);
        const response = await api.post(`/osint/instagram/${caseId}/fetch`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: opts?.signal,
            timeout: opts?.timeout ?? 180_000,
        });
        return response.data;
    },

    backends: async (): Promise<{ backends: string[] }> => {
        const response = await api.get('/osint/instagram/backends');
        return response.data;
    },

    listRuns: async (caseId: string) => {
        const response = await api.get(`/osint/instagram/${caseId}/runs`);
        return response.data;
    },

    runArtifacts: async (caseId: string, runId: string): Promise<InstagramArtifact[]> => {
        const response = await api.get(`/osint/instagram/${caseId}/runs/${runId}/artifacts`);
        return response.data;
    },

    /** Build canvas-ready Tier-4 nodes/edges from a persisted run (the value loop). */
    graphElements: async (caseId: string, runId: string): Promise<InstagramGraphElements> => {
        const response = await api.get(`/osint/instagram/${caseId}/runs/${runId}/graph-elements`);
        return response.data;
    },

    /** All Instagram profiles harvested into a case, with per-profile artefact counts. */
    listProfiles: async (caseId: string): Promise<{ profiles: InstagramProfile[] }> => {
        const response = await api.get(`/osint/instagram/${caseId}/profiles`);
        return response.data;
    },

    /** Cross-profile interconnection graph (profiles linked by shared artefacts). */
    network: async (caseId: string): Promise<InstagramNetwork> => {
        const response = await api.get(`/osint/instagram/${caseId}/network`);
        return response.data;
    },

    /** Promote harvested profiles + artefacts into the shared Fusion case graph
     * (canonical entities merge with Workbench wallets / bank UPIs). */
    promoteToGraph: async (caseId: string): Promise<{
        entities: number;
        edges: number;
        profiles: number;
        same_person_links?: number;
    }> => {
        const response = await api.post(`/osint/instagram/${caseId}/promote-to-graph`);
        return response.data;
    },

    /** Phone / email / UPI → candidate @handles from harvest/P2P (no IG scrape). */
    resolveSeed: async (
        caseId: string,
        seed: string,
        seedType?: string,
        opts?: { signal?: AbortSignal; timeout?: number },
    ): Promise<{
        status: 'found' | 'not_found' | 'not_tested';
        seed_type: string;
        canonical: string;
        candidates: Array<{ handle: string; sources: string[]; evidence: string[] }>;
        message: string;
        sources_tried?: string[];
    }> => {
        const response = await api.post(`/osint/instagram/${caseId}/resolve-seed`, {
            seed,
            seed_type: seedType || undefined,
        }, {
            signal: opts?.signal,
            timeout: opts?.timeout ?? 30_000,
        });
        return response.data;
    },

    /** Resolve identity pivots for a harvested Instagram profile (phone/email). */
    resolveIdentity: async (caseId: string, handle: string): Promise<{
        handle: string;
        resolved_identity: Record<string, unknown>;
    }> => {
        const h = encodeURIComponent(handle.replace(/^@/, ''));
        const response = await api.post(`/osint/instagram/${caseId}/profiles/${h}/resolve-identity`);
        return response.data;
    },
};

export interface InstagramProfile {
    handle: string;
    full_name?: string | null;
    bio?: string | null;
    external_url?: string | null;
    is_business?: boolean | null;
    post_count?: number | null;
    avatar_url?: string | null;
    latest_run_id?: string | null;
    resolved_identity?: any;
    artifact_count: number;
    by_type: Record<string, number>;
    first_harvested_at?: string | null;
    last_harvested_at?: string | null;
}

export interface InstagramNetwork {
    case_id: string;
    nodes: { data: Record<string, any> }[];
    edges: { data: Record<string, any> }[];
    counts: { profiles: number; shared_artefacts: number; profile_links: number; edges: number };
}

export interface InstagramGraphElements {
    case_id: string;
    run_id: string;
    suspect_handle: string;
    nodes: { data: Record<string, unknown> }[];
    edges: { data: Record<string, unknown> }[];
    counts: { nodes: number; edges: number; skipped: number };
}

// ---- Operator Cluster types (case-level operator overview) ----
export interface OperatorClusterEvidence {
    signal: 'kyc_join' | 'same_evm_address' | 'btc_cospend' | 'manual_add' | string;
    weight: number;
    details?: Record<string, any>;
}
export interface OperatorClusterMember {
    chain: string;
    address: string;
    confidence: number;
    evidence: OperatorClusterEvidence[];
    added_by: string;     // 'auto' or stringified user_id
    added_at?: string | null;
}
export interface OperatorClusterKYC {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    exchange_user_id?: string | null;
    exchange_name?: string | null;
    extras?: Record<string, any>;
}
export interface OperatorCluster {
    id: string;
    case_id: string;
    name?: string | null;
    confidence: number;
    kyc: OperatorClusterKYC;
    metadata: Record<string, any>;
    members: OperatorClusterMember[];
    created_at: string;
    created_by?: number | null;
    updated_at: string;
}

// ---- Macro run (Stages 0+1+2 bundled) types ----
export interface LedgerPerChain {
    chain: string;
    tx_count: number;
    deposits: number;
    withdrawals: number;
    self_addresses: number;
    in_counterparties: number;
    out_counterparties: number;
    first_seen: string | null;
    last_seen: string | null;
}
export interface LedgerSummary {
    per_chain: LedgerPerChain[];
    totals: { tx_count: number; deposits: number; withdrawals: number; chains: number };
    regime: { label: string; explanation: string };
    time_profile: {
        first_seen: string | null;
        last_seen: string | null;
        days_active: number | null;
        txs_per_day: number | null;
    };
    top_counterparties_in: Array<{ chain: string; address: string; tx_count: number }>;
    top_counterparties_out: Array<{ chain: string; address: string; tx_count: number }>;
}
export interface LabelledCounterparty {
    chain: string;
    address: string;
    tx_count: number;
    sender_count: number;
    recipient_count: number;
    role: 'sender' | 'recipient' | 'both' | string;
    label?: string | null;
    category?: string | null;
    subcategory?: string | null;
    source?: string | null;
    risk_level?: string | null;
    tags: string[];
}
export interface EntityAttribution {
    counterparties: LabelledCounterparty[];
    totals: { unique: number; labelled: number; unlabelled: number };
}
export interface MacroRunResponse {
    ledger: LedgerSummary;
    entities: EntityAttribution;
    cluster: OperatorCluster;
}

// ---- Stage 3: per-wallet on-chain context ----
// Compact tx record kept on each labelled counterparty for the UI's
// expand-on-click drilldown.
export interface CounterpartyTxRef {
    tx_hash?: string | null;
    value?: string | number | null;
    token?: string | null;
    timestamp?: string | null;
    direction?: 'incoming' | 'outgoing' | 'unknown' | string | null;
    from_address?: string | null;
    to_address?: string | null;
    member_chain?: string | null;
    member_address?: string | null;
}
export interface OnchainContextNewCounterparty {
    chain: string;
    address: string;
    tx_count: number;
    label?: string | null;
    category?: string | null;
    risk_level?: string | null;
    tags: string[];
    transactions?: CounterpartyTxRef[];
}
// ---- Stage 5: pattern detector + AI narrative ----
export interface DetectedPattern {
    id: string;
    severity: 'info' | 'low' | 'medium' | 'high' | 'critical' | string;
    title: string;
    summary: string;
    evidence?: Record<string, any>;
}
export interface MacroSynthesisResponse {
    patterns: DetectedPattern[];
    narrative: string;
    generated_at: string;
    llm_provider?: string | null;
    ai_used: boolean;
}

// ---- Stage 4: cluster-level trace ----
export interface ConsolidatedCounterparty {
    chain: string;
    address: string;
    tx_count: number;
    seen_in_members: number;
    label?: string | null;
    category?: string | null;
    risk_level?: string | null;
    tags: string[];
    transactions?: CounterpartyTxRef[];
}
export interface ClusterTraceMemberSummary {
    chain: string;
    address: string;
    onchain_tx_count: number;
    post_window: number;
    percentage_outside: number;
    subpoena_window_known: boolean;
    new_counterparty_count: number;
}
export interface ClusterTraceResponse {
    members_requested: number;
    members_traced: number;
    members_failed: number;
    off_ramps: ConsolidatedCounterparty[];
    high_risk_hits: ConsolidatedCounterparty[];
    consolidated_counterparties: ConsolidatedCounterparty[];
    per_member_summary: ClusterTraceMemberSummary[];
    window_totals: {
        members_with_window: number;
        pre_window_total: number;
        inside_window_total: number;
        post_window_total: number;
    };
    onchain_totals: { tx_count_returned: number; in_count: number; out_count: number };
    totals: { off_ramps_unique: number; high_risk_unique: number; consolidated_unique: number };
}

export interface OnchainContextResponse {
    wallet: { chain: string; address: string };
    fetched_at: string;
    data_source?: string | null;
    onchain: {
        tx_count_returned: number;
        tx_count_known: number;
        in_count: number;
        out_count: number;
        first_seen: string | null;
        last_seen: string | null;
    };
    subpoena_window: {
        first: string | null;
        last: string | null;
        link_count: number;
    };
    window_comparison: {
        inside_window: number;
        pre_window: number;
        post_window: number;
        percentage_outside: number;
        subpoena_window_known: boolean;
    };
    new_counterparties: OnchainContextNewCounterparty[];
}

export const operatorService = {
    getClusterForCase: async (caseId: string): Promise<OperatorCluster | null> => {
        try {
            const response = await api.get(`/operators/case/${caseId}/cluster`);
            return response.data;
        } catch (e: any) {
            if (e?.response?.status === 404) return null;
            throw e;
        }
    },

    autoCluster: async (
        caseId: string,
        body: { seed_address?: string; seed_chain?: string } = {},
    ): Promise<OperatorCluster> => {
        const response = await api.post(`/operators/case/${caseId}/cluster/auto`, body);
        return response.data;
    },

    /**
     * Bundled Stages 0+1+2 — the action behind the "Run Stages 0–2" button.
     * Returns ledger summary + entity attribution + operator cluster in one call.
     */
    runMacro: async (
        caseId: string,
        body: { seed_address?: string; seed_chain?: string } = {},
    ): Promise<MacroRunResponse> => {
        const response = await api.post(`/operators/case/${caseId}/macro/run`, body);
        return response.data;
    },

    getCluster: async (clusterId: string): Promise<OperatorCluster> => {
        const response = await api.get(`/operators/cluster/${clusterId}`);
        return response.data;
    },

    /**
     * Stage 3 (paid). Pulls recent on-chain activity for one operator wallet
     * and overlays it on the case's subpoena window. Per-wallet — never
     * auto-bundled. ~3–10s wall-time depending on chain.
     */
    fetchOnchainContext: async (
        caseId: string,
        chain: string,
        address: string,
    ): Promise<OnchainContextResponse> => {
        const response = await api.post(
            `/operators/case/${caseId}/onchain-context`,
            { chain, address },
            { timeout: 60_000 },  // chain APIs can be slow; 60s is plenty for a single wallet
        );
        return response.data;
    },

    /**
     * Stage 4 (paid). Cluster-level fan-out — runs Stage 3 against the top-N
     * cluster members in parallel and consolidates off-ramps + high-risk
     * hits + new counterparties. ~10-60s wall-time depending on member count
     * and chain mix. Bounded at 5 concurrent on-chain pulls server-side.
     */
    runClusterTrace: async (
        caseId: string,
        body: { max_members?: number; chain_filter?: string[] } = {},
    ): Promise<ClusterTraceResponse> => {
        const response = await api.post(
            `/operators/case/${caseId}/cluster-trace`,
            body,
            { timeout: 180_000 },  // Stage 4 fan-out can run up to a few minutes
        );
        return response.data;
    },

    /**
     * Stage 5. Pattern detector + AI narrative over the Stage 0–4 outputs.
     * Frontend ships the data we already have so the backend does not refetch.
     * Wall-time bounded by the LLM call (~5–15s).
     */
    synthesizeMacro: async (
        caseId: string,
        body: {
            ledger: LedgerSummary;
            entities: EntityAttribution;
            cluster: OperatorCluster;
            cluster_trace?: ClusterTraceResponse | null;
            // Per-wallet on-chain pulls (Stage 3) keyed by "{chain}:{address}".
            // Optional — empty if the user hasn't clicked "Pull on-chain" on any
            // cluster member. When present, every pulled wallet's window
            // comparison + new counterparties feed into pattern detection +
            // the LLM narrative.
            onchain_contexts?: Record<string, OnchainContextResponse>;
        },
    ): Promise<MacroSynthesisResponse> => {
        const response = await api.post(
            `/operators/case/${caseId}/macro/synthesize`,
            body,
            { timeout: 120_000 },
        );
        return response.data;
    },

    patchMembers: async (
        clusterId: string,
        body: {
            add?: Array<{ chain: string; address: string }>;
            remove?: Array<{ chain: string; address: string }>;
            rename?: string;
        },
    ): Promise<OperatorCluster> => {
        const response = await api.patch(`/operators/cluster/${clusterId}/members`, body);
        return response.data;
    },
};

// ---- Cloud forensic parse: tiered address rows ----
export interface ForensicExtractedAddress {
    address: string;
    chain: string;
    /** 'verified' = checksum-proven; 'weak' = structural match only. Absent on pre-tiering servers. */
    confidence?: 'verified' | 'weak';
    confidence_reason?: string | null;
    source?: string;
    sources?: string[];
    occurrences?: number;
    metadata?: any;
}

export interface ForensicParseResult {
    extracted_addresses: ForensicExtractedAddress[];
    tx_hashes?: any[];
    addresses_verified_total?: number;
    addresses_weak_total?: number;
    summary?: Record<string, any> | null;
    [key: string]: any;
}

// ---- Forensic overlay types (Phase 2 canvas bridge) ----
export interface ForensicOverlayKYC {
    names: string[];
    emails: string[];
    exchanges: string[];
    exchange_user_ids: string[];
}
export interface ForensicOverlayNode {
    address: string;
    chain: string;
    /** forensic_ghost = placeholder for a counterparty the exchange did not disclose.
     *  Address is `ghost:{link_id}:source`, will be promoted to a real address
     *  later if an on-chain match is found. */
    kind: 'forensic_self' | 'forensic_counterparty' | 'forensic_ghost';
    labels: string[];
    kyc: ForensicOverlayKYC;
    record_ids: string[];
    movement_count: number;
}
export interface ForensicOverlayEdge {
    source_address: string | null;
    source_chain: string | null;
    target_address: string;
    target_chain: string;
    tx_hash: string | null;
    exchange_internal_id: string | null;
    direction: 'deposit' | 'withdrawal';
    amount: string | null;
    currency: string | null;
    timestamp: string | null;
    record_id: string;
    verified: boolean;
    /** True iff verified AND counterparty present — the OnChain↔OffChain anchor */
    is_proof_anchor: boolean;
    /** True when the counterparty side is a ghost (exchange withheld it) */
    is_undisclosed?: boolean;
    /** UTXO multi-input/output: same vocabulary the on-chain BTC fetcher uses.
     *  utxo_secondary=true on every non-primary edge of a multi-sender (or
     *  multi-recipient) tx. all_input_addresses / all_output_addresses give the
     *  full sibling set for inspector grouping. */
    utxo_secondary?: boolean;
    all_input_addresses?: string[];
    all_output_addresses?: string[];
}
export interface ForensicOverlayRecord {
    id: string;
    file_name: string;
    source_exchange: string | null;
    record_type: string;
    file_hash: string;
    created_at: string | null;
    created_by: number | null;
    customer: {
        name?: string | null;
        email?: string | null;
        exchange_name?: string | null;
        exchange_user_id?: string | null;
    };
    movements_total: number;
    movements_with_hash: number;
    movements_internal_id_only: number;
    parser: string | null;
}
export interface ForensicOverlaySummary {
    records_count: number;
    addresses_count: number;
    edges_count: number;
    verified_edges: number;
    internal_only_edges: number;
    proof_anchor_edges: number;
    self_addresses: number;
    counterparty_addresses: number;
    ghost_addresses?: number;
    undisclosed_edges?: number;
}
export interface ForensicOverlay {
    case_id: string;
    records: ForensicOverlayRecord[];
    nodes: ForensicOverlayNode[];
    edges: ForensicOverlayEdge[];
    summary: ForensicOverlaySummary;
}

/**
 * The synchronous auto-investigate route streams its answer (keep-alive spaces, then JSON), so its
 * HTTP status is 200 before the run ends. A run that fails after that point is the whole body:
 * `{ detail, status_code }`. Rethrow it in the shape an axios error has, so callers see a failure.
 */
export function throwIfStreamedRunError<T>(data: T): T {
    const d = data as unknown as Record<string, unknown> | null;
    if (d && typeof d === 'object' && !Array.isArray(d)
        && Object.keys(d).length === 2 && 'detail' in d
        && typeof d.status_code === 'number' && d.status_code >= 400) {
        const err: Error & { response?: { status: number; data: { detail: unknown } } } =
            new Error(typeof d.detail === 'string' ? d.detail : 'Auto investigation failed');
        err.response = { status: d.status_code, data: { detail: d.detail } };
        throw err;
    }
    return data;
}

export const caseWorkerService = {
    run: async (payload: { case_id: string; case_title?: string; seeds: string[]; notes?: string; user_sso_token?: string }) => {
        const response = await api.post('/case-worker/run', payload);
        return response.data;
    },
    autoInvestigateCase: async (caseId: string, payload?: { seeds?: string[]; case_title?: string; notes?: string; user_sso_token?: string }) => {
        // Per-request override (as addressService.trace() does): the server-side
        // fan-out has a 600 s wall-clock budget; the 30 s default would abort the
        // request while the server keeps running.
        const response = await api.post(`/case-worker/cases/${caseId}/auto-investigate`, payload || {}, { timeout: 600000 });
        return throwIfStreamedRunError(response.data);
    },
    /**
     * Start the case's Auto-Investigate run as a background `case_worker` canvas
     * job (`?async=1`). The server answers 202 with the job envelope at once, or
     * 200 with `existing: true` when a run is already going for the case (R4).
     * Progress is read from `canvasJobService.get` (or the envelope's `sse`).
     */
    startAutoInvestigateJob: async (
        caseId: string,
        payload?: { seeds?: string[]; case_title?: string; notes?: string; user_sso_token?: string; persist_snapshot?: boolean },
    ): Promise<CanvasJobEnvelope> => {
        const response = await api.post(
            `/case-worker/cases/${encodeURIComponent(caseId)}/auto-investigate`,
            payload || {},
            { params: { async: 1 }, timeout: 60000 },
        );
        return response.data;
    },
};

/** Case-level report data (GET /cases/{id}/report-data): one aggregation over the
 *  case spine and the latest case-worker run. Read-only. */
export const caseReportService = {
    /** `runId` (a run's event id or run id) selects one run; omitted, the latest run is used. */
    getReportData: async (caseId: string, runId?: string, sections?: readonly string[]): Promise<import('../types/fusion').CaseReportData> => {
        // OPEN01: `sections` returns only those top-level keys (the page fetches what it shows first, then the rest).
        const params: Record<string, string> = {};
        if (runId) params.run_id = runId;
        if (sections && sections.length) params.sections = sections.join(',');
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/report-data`, {
            timeout: 120000,
            ...(Object.keys(params).length ? { params } : {}),
        });
        return response.data;
    },
};

// ── Claim ledger over a stored report (CHECK01 / REPORT05) ─────────────────

export interface ClaimItem {
    id: string;
    kind: string;
    text?: string;
    span?: string;
    claim?: string;
    verdict: 'A' | 'D' | 'E' | string;
    rule?: string;
    message?: string;
    [key: string]: unknown;
}

export interface ClaimLedger {
    evidence_id: string | number;
    evidence_type?: string;
    title?: string;
    checker?: string;
    status: 'checked' | 'no_text' | 'refused' | string;
    reason?: string;
    claims: ClaimItem[];
    counts: { A?: number; D?: number; E?: number; [k: string]: number | undefined };
    summary?: string;
    extraction?: Record<string, unknown>;
    binding?: {
        status: 'bound' | 'legacy_unverified' | string;
        case_id?: string;
        evidence_id?: string | number;
        run_event_id?: string | null;
        run_id?: string | null;
        record_sha256?: string | null;
        document_sha256?: string | null;
        report_text_sha256?: string | null;
        reason?: string;
    };
    [key: string]: unknown;
}

export const claimCheckService = {
    getClaimLedger: async (caseId: string, evidenceId: string | number, runRef?: string): Promise<ClaimLedger> => {
        const response = await api.get(
            `/cases/${encodeURIComponent(caseId)}/evidence/${encodeURIComponent(String(evidenceId))}/claim-ledger`,
            { ...(runRef ? { params: { run_ref: runRef } } : {}) }
        );
        return response.data;
    },
    checkClaims: async (caseId: string, evidenceId: string | number, runRef?: string): Promise<ClaimLedger> => {
        const response = await api.post(
            `/cases/${encodeURIComponent(caseId)}/evidence/${encodeURIComponent(String(evidenceId))}/claim-ledger`,
            null,
            { ...(runRef ? { params: { run_ref: runRef } } : {}) }
        );
        return response.data;
    },
};

// ── Finding review status, assignment and comments (CASEFLOW01) ──────────────

export interface FindingItem {
    row_key: string;
    role: string;
    chain: string;
    address: string;
    status: import('../utils/findingVocabulary').FindingStatus;
    assignee_id?: number | null;
    reason?: string | null;
    comments?: Array<{ id?: string; actor_id?: number; text: string; ts?: string }>;
    updated_at?: string | null;
    history?: Array<{ type: string; ts?: string; actor_id?: number; [k: string]: unknown }>;
    in_latest_run?: boolean;
    target?: Record<string, unknown>;
}

export interface FindingsResponse {
    case_id: string;
    findings: FindingItem[];
    by_key: Record<string, FindingItem>;
}

export const caseFindingsService = {
    getFindings: async (caseId: string): Promise<FindingsResponse> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/findings`);
        return response.data;
    },
    updateFinding: async (
        caseId: string,
        rowKey: string,
        data: {
            action?: 'assign' | 'status' | 'comment';
            assignee_id?: number | null;
            status?: string;
            reason?: string;
            text?: string;
            comment?: string;
        },
    ): Promise<{ ok: boolean; event_id: string; row_key: string; event_type: string }> => {
        const response = await api.post(
            `/cases/${encodeURIComponent(caseId)}/findings/${encodeURIComponent(rowKey)}`,
            data,
        );
        return response.data;
    },
};

// ── Case narrative over the run record (NARR01) ─────────────────────────────

export interface CaseNarrativeSentence { text: string; cites: string[]; template?: boolean }
export interface CaseNarrativeDropped { text: string; cites: string[]; reasons: string[]; section?: string }
export interface CaseNarrativeSection {
    key: string;
    title: string;
    /** EXPLAIN01: the report section (its DOM id) this part explains; absent on blocks stored before. */
    report_section?: string | null;
    dropped_count?: number;
    model?: string | null;
    model_returned?: string | null;
    status?: string;
    generation_id?: string | null;
    template_used?: boolean;
    sentences: CaseNarrativeSentence[];
    dropped?: CaseNarrativeDropped[];
}
export interface CaseNarrativeBlock {
    schema: string;
    label?: string;
    status: string;
    /** Run identity echoed by the server, used to keep a cached block bound to its report. */
    run_event_id?: string;
    run_id?: string | null;
    generation_id: string;
    record_sha256: string;
    prompt_sha256?: string;
    provider?: string | null;
    model?: string | null;
    model_returned?: string | null;
    tokens_in?: number | null;
    tokens_out?: number | null;
    cost_usd?: number | null;
    sections: CaseNarrativeSection[];
    dropped: CaseNarrativeDropped[];
    explanation_label?: string;
    sections_version?: number;
    /** EXPLAIN01: failures of the review rubric's explanation_cites check (deterministic, no model). */
    review_findings?: { id?: string; check?: string; severity?: string; statement?: string; cites?: string[]; section?: string; state?: string; would_settle?: string; confidence?: string }[];
    counts?: { model_sentences_kept?: number; dropped?: number; template_sentences?: number };
    facts?: { id: string; kind: string; text: string; gate?: string }[];
    cached?: boolean;
}

export const caseNarrativeService = {
    /** The cached narrative of a run (`runRef`: event id, run id or `latest`); 404 when none, 403 when turned off. */
    get: async (caseId: string, runRef: string): Promise<CaseNarrativeBlock> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runRef)}/narrative`);
        return response.data;
    },
    /** Generate (or return the narrative cached for the same record digest). Four model calls; allow time. */
    generate: async (caseId: string, runRef: string): Promise<CaseNarrativeBlock> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runRef)}/narrative`,
            undefined, { timeout: 240000 });
        return response.data;
    },
};

// ── Officer pivot (PIVOT01): one bounded question of a finished record ───────────────────

export const casePivotService = {
    /** The stored pivots of a case, newest first; `runRef` (event id or run id) limits them to one run. */
    list: async (caseId: string, runRef?: string): Promise<import('../types/pivot').PivotBlock[]> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/pivots`,
            runRef ? { params: { run_id: runRef } } : undefined);
        return response.data?.pivots ?? [];
    },
    /** Start the `pivot` operator as a canvas job; follow it with `canvasJobService.get` (the final progress carries `pivot_id`). */
    start: async (caseId: string, p: { run_id: string; question: string; budget: { seconds: number; pages: number } }): Promise<CanvasJobEnvelope> => {
        return canvasJobService.start(caseId, 'pivot', { params: { ...p } });
    },
    /** Put rows the pivot found on the graph (`promote_pivot_rows`): the wallets as context entities, the transfer as one edge. Officer's click only. */
    promote: async (caseId: string, p: { run_id: string; question_id: string; row_ids: string[] }): Promise<CanvasJobEnvelope> => {
        return canvasJobService.start(caseId, 'promote_pivot_rows', { params: { ...p } });
    },
};

// ── Review again with the larger model (REVIEW02) ───────────────────────────────────────

export const caseReviewService = {
    /** Start the `review` operator as a canvas job: the stored record's review is run again with the larger model and the
     *  new pass is kept beside the earlier ones. Follow it with `canvasJobService.get`; read the result from report-data. */
    start: async (caseId: string, p: { run_id: string }): Promise<CanvasJobEnvelope> => {
        return canvasJobService.start(caseId, 'review', { params: { run_id: p.run_id, model: 'large' } });
    },
};

// ── Shareable report links (owner side) ──────────────────────────────────────

export interface ShareLinkView {
    opened_at: string;
    outcome: 'opened' | 'wrong_passcode' | 'expired' | 'revoked' | 'exhausted' | string;
    viewer_name: string | null;
    viewer_org: string | null;
    viewer_email: string | null;
    ip: string | null;
    user_agent: string | null;
}

export interface ShareLink {
    id: string;
    case_id: string;
    run_id: string | null;
    label: string;
    created_at: string;
    expires_at: string;
    revoked_at: string | null;
    max_views: number | null;
    view_count: number;
    has_passcode: boolean;
    status: 'active' | 'revoked' | 'expired' | 'exhausted';
    views: ShareLinkView[];
}

export interface CreateShareLinkBody {
    expires_in_hours?: number;
    passcode?: string;
    label?: string;
    max_views?: number;
    run_id?: string;
}

/** Owner side of shared report links. The create response carries the plaintext URL once. */
export const reportShareService = {
    create: async (caseId: string, body: CreateShareLinkBody): Promise<ShareLink & { url: string }> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/share-links`, body);
        return response.data;
    },
    list: async (caseId: string): Promise<{ links: ShareLink[] }> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/share-links`);
        return response.data;
    },
    revoke: async (caseId: string, linkId: string): Promise<{ revoked: boolean; link_id: string }> => {
        const response = await api.delete(`/cases/${encodeURIComponent(caseId)}/share-links/${encodeURIComponent(linkId)}`);
        return response.data;
    },
};

// ── Screening sweep (NS-60 / NS-60B) ─────────────────────────────────────────

/** The sweep is synchronous for up to SCREENING_SWEEP_BUDGET_S (120 s) plus the store; the 30 s default would abort it. */
export const SCREEN_TIMEOUT_MS = 180000;

export type ScreeningSweep = import('../components/report/types').ScreeningResult & {
    event_id?: string | null;
    continued?: boolean;
};

export interface ScreeningSuppressionPayload {
    hit_key?: string;
    canonical_id?: string;
    kind?: string;
    source?: string;
    rule?: string;
    label?: string;
    reason: string;
    action?: 'suppress' | 'unsuppress';
}

export interface ScreeningSuppressionRecord {
    canonical_id?: string | null;
    kind?: string;
    source?: string;
    rule?: string;
    label?: string;
    hit_key?: string;
    reason?: string;
    officer?: string;
    time?: string;
}

export interface ScreeningSuppressionsResponse {
    suppressions: ScreeningSuppressionRecord[];
    history: Array<Record<string, unknown>>;
}

export const caseScreeningService = {
    /** Screen the case's identifiers by priority tier; `continueSweep` screens the next batch of the latest sweep. */
    run: async (caseId: string, opts: { continueSweep?: boolean } = {}): Promise<ScreeningSweep> => {
        const response = await api.post(
            `/cases/${encodeURIComponent(caseId)}/screen`,
            {},
            { timeout: SCREEN_TIMEOUT_MS, ...(opts.continueSweep ? { params: { continue: 1 } } : {}) },
        );
        return response.data;
    },
    /** The latest stored sweep; 404 when none has been run. */
    latest: async (caseId: string): Promise<ScreeningSweep> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/screen`);
        return response.data;
    },
    /** Suppress a screening hit with an officer reason (ALLOW01). */
    suppress: async (caseId: string, data: ScreeningSuppressionPayload): Promise<{ event_id: string; status: string; hit_key: string; screening?: ScreeningSweep }> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/screening/suppressions`, { ...data, action: 'suppress' });
        return response.data;
    },
    /** Unsuppress a previously suppressed screening hit with an officer reason (ALLOW01). */
    unsuppress: async (caseId: string, data: ScreeningSuppressionPayload): Promise<{ event_id: string; status: string; hit_key: string; screening?: ScreeningSweep }> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/screening/suppressions`, { ...data, action: 'unsuppress' });
        return response.data;
    },
    /** List active suppressions and event history for a case (ALLOW01). */
    listSuppressions: async (caseId: string): Promise<ScreeningSuppressionsResponse> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/screening/suppressions`);
        return response.data;
    },
};

// ── Related cases (NS-58) ────────────────────────────────────────────────────

export interface CaseLinkView {
    id: string;
    case_id: string;
    related_case_id: string;
    /** The other case's title when the caller can open it, otherwise null. */
    title: string | null;
    visible: boolean;
    reason: string;
    shared_identifiers: { type?: string | null; value_prefix?: string | null }[];
    created_by?: number | null;
    created_at?: string | null;
}

export interface CreateCaseLinkBody {
    related_case_id: string;
    /** May be left empty with `from_candidate`: the server then writes its default reason. */
    reason?: string;
    /** The link is made from the run's advisory candidate. */
    from_candidate?: boolean;
    /** Omitted or empty: the server records the wallets the two cases share. */
    shared_identifiers?: { type: string; value: string }[];
}

/** Writes need edit rights on the server (owner, owner's admin, super admin, editor collaborator); the UI shows them only when the case GET says `can_edit`. */
export const caseLinksService = {
    list: async (caseId: string): Promise<{ links: CaseLinkView[] }> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/links`);
        return response.data;
    },
    create: async (caseId: string, body: CreateCaseLinkBody): Promise<CaseLinkView> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/links`, body);
        return response.data;
    },
    remove: async (caseId: string, linkId: string): Promise<{ deleted: boolean; link_id: string }> => {
        const response = await api.delete(`/cases/${encodeURIComponent(caseId)}/links/${encodeURIComponent(linkId)}`);
        return response.data;
    },
};

// ── Case facts (INTAKE01) ────────────────────────────────────────────────────

export type CaseKind = 'exploit' | 'fraud_inflow' | 'extortion' | 'hawala_layering' | 'other' | 'unknown';
export type StatedClaimKind = 'total' | 'channel_total' | 'duration' | 'count';
export type StatedClaimSource = 'FIR' | 'complaint' | 'prior report' | 'officer';

/** A figure someone stated; the run checks it and prints it beside the traced one. */
export interface StatedClaim {
    kind: StatedClaimKind;
    value: number;
    unit: string;
    channel?: string | null;
    path?: string | null;
    /** Durations: the two transfers (tx hashes) the duration spans. */
    from_ref?: string | null;
    to_ref?: string | null;
    source: StatedClaimSource;
    note?: string | null;
}

/** Officer inputs, never findings. Every field optional; an absent field is unknown. */
export interface CaseFacts {
    case_kind?: CaseKind | null;
    offence_window?: { start: string; end?: string | null } | null;
    stated_claims?: StatedClaim[];
}

export interface CaseFactsRecord {
    case_id: string;
    facts: CaseFacts;
    /** 0 when the facts were never set. */
    version: number;
    set_by?: number | null;
    set_by_username?: string | null;
    set_at?: string | null;
    changed?: string[];
    field_set?: Record<string, { by?: { id?: number | null; username?: string | null }; at?: string }>;
    /** Whether the caller may change the facts (owner, owner's admin, super admin, editor collaborator). */
    can_edit?: boolean | null;
}

/** PUT needs edit rights on the server; the card shows the editor only when GET says `can_edit`. */
export const caseFactsService = {
    get: async (caseId: string): Promise<CaseFactsRecord> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/facts`);
        return response.data;
    },
    put: async (caseId: string, facts: CaseFacts): Promise<CaseFactsRecord> => {
        const response = await api.put(`/cases/${encodeURIComponent(caseId)}/facts`, facts);
        return response.data;
    },
};

// ── Case runs and canvas jobs (W3-07) ────────────────────────────────────────

/** Progress of a running canvas job, as the `progress` column stores it. */
export interface CanvasJobProgress {
    /** Step name (e.g. `deep_fanout`). */
    step?: string;
    /** Step number in the run's own numbering (e.g. `3b`). */
    step_number?: string | number;
    /** 1-based position among `total_steps`. */
    step_index?: number;
    total_steps?: number;
    message?: string;
    counts?: Record<string, number | string | null>;
    run_id?: string;
    percent?: number;
    /** The `pivot` operator's final line: the stored pivot block's id and its status. */
    pivot_id?: string;
    pivot_status?: string;
}

export type CanvasJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/** `GET /canvas/{case_id}/jobs/{job_id}`: the live job, else its persisted row. */
export interface CanvasJobView {
    job_id: string;
    case_id?: string | null;
    operator?: string | null;
    status: CanvasJobStatus | string;
    progress?: CanvasJobProgress | null;
    params?: Record<string, unknown> | null;
    target_ref?: string | null;
    /** The run's `case_worker_run` event id once the run was recorded. */
    result_ref?: string | null;
    error?: string | null;
    created_by?: number | null;
    created_at?: string | null;
    started_at?: string | null;
    ended_at?: string | null;
    updated_at?: string | null;
    live?: boolean;
    sse?: string;
    status_url?: string;
}

/** Answer to a job start (`?async=1` or `POST /canvas/{id}/jobs`). */
export interface CanvasJobEnvelope {
    job_id: string;
    operator?: string;
    status?: CanvasJobStatus | string;
    /** True when a run was already going for the case and that job came back. */
    existing?: boolean;
    progress?: CanvasJobProgress | null;
    sse?: string;
    status_url?: string;
}

/** One case-worker run (`GET /cases/{case_id}/runs`). */
export interface CaseRun {
    /** The `case_worker_run` event id; null while the job has not recorded one. */
    event_id: string | null;
    run_id: string | null;
    job_id: string | null;
    status: string | null;
    job_status: string | null;
    started_at: string | null;
    ended_at: string | null;
    duration_s: number | null;
    seed_count: number | null;
    node_count: number | null;
    edge_count: number | null;
    counts_source?: string | null;
    readiness?: unknown;
    deep?: boolean | null;
    budget_capped?: boolean | null;
    recorded_at?: string | null;
    actor_id?: number | null;
    summary?: Record<string, number> | null;
    progress?: CanvasJobProgress | null;
    error?: string | null;
    /** Next steps the run offered (W3-09b); null for runs recorded before the direction step. */
    next_steps_offered?: number | null;
    /** Read-only passes the run executed automatically (AI_DIRECTION_AUTORUN). */
    autorun_executed?: number | null;
    direction_method?: string | null;
    /** True when no prompt left the server during the run. */
    no_model_call?: boolean | null;
    /** Model calls the data-residency policy refused. */
    blocked_calls?: number | null;
}

export interface CaseRunsResponse {
    case_id: string;
    runs: CaseRun[];
    count: number;
    /** Rows in case_events. Null when the live count could not be read. */
    journal_count?: number | null;
    /** Rows in case_entities / case_edges (the spine, not the collapsed macro view). */
    graph_counts?: { nodes: number | null; edges: number | null; source?: string | null } | null;
}

export const caseRunsService = {
    /** The case's runs, newest first; running jobs are included with their progress. */
    list: async (caseId: string, limit?: number): Promise<CaseRunsResponse> => {
        const response = await api.get(`/cases/${encodeURIComponent(caseId)}/runs`, {
            ...(limit ? { params: { limit } } : {}),
        });
        return response.data;
    },
};

export const canvasJobService = {
    /** `POST /canvas/{case_id}/jobs`. Background operators (`case_worker`) start at once. */
    start: async (
        caseId: string,
        operator: string,
        opts?: { target_ref?: string; params?: Record<string, unknown> },
    ): Promise<CanvasJobEnvelope> => {
        const response = await api.post(
            `/canvas/${encodeURIComponent(caseId)}/jobs`,
            { operator, target_ref: opts?.target_ref, params: opts?.params || {} },
            { timeout: 60000 },
        );
        return response.data;
    },
    /** `GET /canvas/{case_id}/jobs/{job_id}`: status, latest progress, result_ref, error. */
    get: async (caseId: string, jobId: string): Promise<CanvasJobView> => {
        const response = await api.get(`/canvas/${encodeURIComponent(caseId)}/jobs/${encodeURIComponent(jobId)}`);
        return response.data;
    },
    /** `POST /canvas/{case_id}/jobs/{job_id}/cancel`. The job stops within one heartbeat. */
    cancel: async (caseId: string, jobId: string): Promise<CanvasJobView> => {
        const response = await api.post(
            `/canvas/${encodeURIComponent(caseId)}/jobs/${encodeURIComponent(jobId)}/cancel`,
        );
        return response.data;
    },
};


// ── Data sources: manifest, run ledger and source candidates (W3-01, W3-02) ──

/** One row of `GET /admin/sources` (manifest row joined with its latest harvest_runs row). */
export interface ManifestSource {
    id: string;
    name: string;
    cadence: string;
    status: string;
    kind: string;
    lane: string;
    table: string;
    evidence_rule: string;
    evidence_enforced: boolean;
    legal_gate: string;
    proxy: string;
    owner: string;
    origin: string;
    runs: boolean;
    next_run_at: string | null;
    last_status: string | null;
    last_run_at: string | null;
    last_success_at: string | null;
    last_rows_written: number | null;
    last_rows_withheld: number | null;
}

export interface ManifestSourcesResponse {
    manifest: string;
    generated_at: string;
    /** ok | missing_table | unavailable | error: whether run data could be read. */
    ledger: string;
    counts: Record<string, { total: number; active: number }>;
    unknown_fields: { source_id: string; field: string }[];
    sources: ManifestSource[];
}

export interface SourceChecklistQuestion {
    key: string;
    label: string;
    help: string;
    /** text | enum | list | table | source_id */
    kind: string;
    choices: string[];
    required: boolean;
    section: string;
}

export type SourceReviewAnswers = Record<string, string | string[]>;

export interface SourceCandidate {
    id: string;
    case_id: string | null;
    proposed_by: number | null;
    proposed_by_name: string | null;
    url: string;
    url_hash: string;
    what_it_showed: string;
    evidence_ref: string | null;
    evidence_sha256: string | null;
    legal_note: string | null;
    /** proposed | under_review | approved | rejected */
    status: string;
    review_answers: SourceReviewAnswers;
    reviewer_name: string | null;
    reviewed_at: string | null;
    approved_by_name: string | null;
    approved_at: string | null;
    approved_manifest_id: string | null;
    manifest_stanza: string | null;
    reject_reason: string | null;
    created_at: string;
    updated_at: string;
    missing_answers: string[];
    /** Present on list rows: read from the manifest and the run ledger. */
    manifest_status?: string | null;
    ledger_runs?: number | null;
    lifecycle_state?: string;
    /** Present on the POST response: other open candidates with the same URL hash. */
    open_duplicates?: number;
}

export interface SourceCandidatesResponse {
    candidates: SourceCandidate[];
    ledger: string;
    checklist: SourceChecklistQuestion[];
    required: string[];
    statuses: string[];
}

export interface SourceCandidateProposal {
    url: string;
    what_it_showed: string;
    evidence_ref?: string;
    evidence_sha256?: string;
    legal_note?: string;
}

export const sourcesService = {
    /** `GET /admin/sources` (super-admin): manifest rows with their last ledger run. */
    listSources: async (): Promise<ManifestSourcesResponse> => {
        const response = await api.get('/admin/sources');
        return response.data;
    },
    /** `GET /admin/source-candidates`: candidates newest first plus the review checklist. */
    listCandidates: async (params?: { status?: string; case_id?: string }): Promise<SourceCandidatesResponse> => {
        const response = await api.get('/admin/source-candidates', params ? { params } : undefined);
        return response.data;
    },
    /** `POST /cases/{case_id}/source-candidates`: an officer files a source met in case work. */
    propose: async (caseId: string, body: SourceCandidateProposal): Promise<SourceCandidate> => {
        const response = await api.post(`/cases/${encodeURIComponent(caseId)}/source-candidates`, body);
        return response.data;
    },
    /** `POST /admin/source-candidates/{id}/review`: save (part of) the checklist answers. */
    review: async (candidateId: string, answers: SourceReviewAnswers): Promise<SourceCandidate> => {
        const response = await api.post(`/admin/source-candidates/${encodeURIComponent(candidateId)}/review`, { answers });
        return response.data;
    },
    /** `POST /admin/source-candidates/{id}/approve`: approve for build; returns the manifest stanza. */
    approve: async (candidateId: string): Promise<SourceCandidate & { manifest_path?: string }> => {
        const response = await api.post(`/admin/source-candidates/${encodeURIComponent(candidateId)}/approve`);
        return response.data;
    },
    /** `POST /admin/source-candidates/{id}/reject`. */
    reject: async (candidateId: string, reason: string): Promise<SourceCandidate> => {
        const response = await api.post(`/admin/source-candidates/${encodeURIComponent(candidateId)}/reject`, { reason });
        return response.data;
    },
};

// ── Next steps of a case-worker run (W3-09b, OD10) ───────────────────────────

export type NextStepCostClass = 'read_only' | 'fetch' | 'paid';
export type NextStepStatus = 'offered' | 'executed' | 'dismissed';

/** One suggestion (`GET /cases/{id}/runs/{run_id}/next-steps`). */
export interface NextStep {
    suggestion_id: string;
    /** A pass-registry id, or a manual step id (`kind: 'action'`). */
    pass_id: string;
    title?: string;
    /** One sentence built from index-keyed evidence (seed#1, cp#2, target#1). */
    reason: string;
    inputs: string[];
    cost_class: NextStepCostClass | string;
    evidence_refs: string[];
    confidence: number;
    /** `rule` unless a model ranking was applied to this run's suggestions. */
    origin: 'rule' | 'model' | string;
    kind: 'pass' | 'action' | string;
    rule?: string;
    /** The case-worker job can run it; manual steps cannot. */
    runnable: boolean;
    rank?: number;
    model_note?: string | null;
    status: NextStepStatus | string;
    executed_by?: 'autorun' | 'officer' | null;
    produced?: boolean | null;
    job_id?: string | null;
    dismissed_reason?: string | null;
    acted_at?: string | null;
    actor_id?: number | null;
}

/** A `_direction_log` entry (automatic or officer-selected execution). */
export interface DirectionLogEntry {
    step?: string | null;
    method?: string;
    selected?: string[];
    executed_by?: string | null;
    suggestion_id?: string | null;
    pass_id?: string;
    reason?: string;
    produced?: boolean;
    status?: string;
    cost_class?: string;
    origin?: string;
}

export interface NextStepsResponse {
    case_id: string;
    run_id: string | null;
    event_id: string | null;
    /** False for runs recorded before the direction step existed. */
    recorded: boolean;
    method: 'deterministic' | 'llm' | 'llm_failed_deterministic' | string | null;
    generation_id: string | null;
    provider?: string | null;
    model?: string | null;
    no_model_call?: boolean | null;
    model_calls?: number | null;
    blocked_calls?: number | null;
    autorun?: { setting?: boolean; eligible_run?: boolean; enabled?: boolean; executed?: string[];
                max_passes?: number; budget_s?: number; budget_exhausted?: boolean } | null;
    suggestions: NextStep[];
    autorun_log: DirectionLogEntry[];
    officer_log?: DirectionLogEntry[];
    refs?: Record<string, { kind?: string; value?: string; entity?: string | null }>;
}

export const caseDirectionService = {
    /** The run's stored suggestions with their status and the automatic-run log. */
    get: async (caseId: string, runId: string): Promise<NextStepsResponse> => {
        const response = await api.get(
            `/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/next-steps`,
        );
        return response.data;
    },
    /** Start the step as a case-worker job (409 while another run is going). */
    run: async (caseId: string, runId: string, suggestionId: string): Promise<CanvasJobEnvelope & { suggestion_id: string; pass_id: string }> => {
        const response = await api.post(
            `/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/next-steps/${encodeURIComponent(suggestionId)}/run`,
            {},
            { timeout: 60000 },
        );
        return response.data;
    },
    /** Set the step aside with a reason. */
    dismiss: async (caseId: string, runId: string, suggestionId: string, reason: string): Promise<NextStep> => {
        const response = await api.post(
            `/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/next-steps/${encodeURIComponent(suggestionId)}/dismiss`,
            { reason },
        );
        return response.data;
    },
};

export default api;

// ── Admin settings: cloud vision for image intake (OD27) ─────────────────────

export interface CloudVisionSetting {
    cloud_vision_enabled: boolean;
    cloud_vision_provider: string;
    provider_key_configured: boolean;
    locked_by_deployment: boolean;
    model: string;
    /** True only when images would actually be sent now (setting on, key present, not locked). */
    effective: boolean;
    reason: string;
    updated_at: string | null;
    updated_by: number | null;
    /** The exact owner-facing warning text, served by the backend so both sides print the same words. */
    warning: string;
}

export const adminSettingsService = {
    /** `GET /admin/settings/cloud-vision` (admin tiers). */
    getCloudVision: async (): Promise<CloudVisionSetting> => {
        const response = await api.get('/admin/settings/cloud-vision');
        return response.data;
    },
    /** `PUT /admin/settings/cloud-vision`: switch image intake's cloud vision engine on or off. */
    setCloudVision: async (enabled: boolean): Promise<CloudVisionSetting> => {
        const response = await api.put('/admin/settings/cloud-vision', { enabled });
        return response.data;
    },
    /** `GET /admin/settings/llm-cost` (super admin): cost ceilings, month-to-date spend, prices. */
    getLlmCost: async (): Promise<LlmCostState> => {
        const response = await api.get('/admin/settings/llm-cost');
        return response.data;
    },
    /** `PUT /admin/settings/llm-cost`: change one of the five cost settings; returns the new state. */
    setLlmCost: async (key: LlmCostKey, value: unknown): Promise<LlmCostState> => {
        const response = await api.put('/admin/settings/llm-cost', { key, value });
        return response.data;
    },
};

// ── Admin settings: model-call cost ceilings (COST01) ────────────────────────

export type LlmCostKey =
    | 'llm_monthly_ceiling_usd'
    | 'llm_alarm_pct'
    | 'llm_max_calls_per_run'
    | 'llm_max_tokens_per_run'
    | 'llm_price_table';

export type LlmPriceTable = Record<string, { in: number; out: number }>;

export interface LlmCostSettingRow {
    key: LlmCostKey;
    label: string;
    unit: string;
    what: string;
    value: number | LlmPriceTable;
    default: number | LlmPriceTable;
    is_default: boolean;
    source: string | null;
    updated_at: string | null;
    updated_by: number | null;
}

export interface LlmCostState {
    values: Record<LlmCostKey, unknown>;
    defaults: Record<LlmCostKey, unknown>;
    settings: LlmCostSettingRow[];
    month: string;
    month_start: string;
    ceiling_usd: number;
    /** Month-to-date spend; null when the generations table could not be read. */
    spent_usd: number | null;
    spend: {
        mtd_usd: number;
        rows_with_cost: number;
        rows_priced_now: number;
        unpriced_rows: number;
        unpriced_no_usage: number;
        unpriced_no_price: number;
        by_model: { model: string | null; stored_usd: number; priced_now_usd: number; rows_with_cost: number; rows_priced_now: number; rows_unpriced: number }[];
    } | null;
    spend_error: string | null;
    alarm: { alarm_pct: number; levels: number[]; pct_of_ceiling: number | null; level: number | null; active: boolean; fired_levels: number[] };
    price_table: { model: string; in: number; out: number; source: 'builtin' | 'env' | 'setting' }[];
    recent_runs: { run_id: string; calls: number; tokens: number }[];
    enforced: boolean;
    not_refused?: string[];
}
